import React, { useState, Component, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Button, Col, Table, Card, Tabs, Tab } from "react-bootstrap";
import Calendar from "react-calendar";

import loadable from "@loadable/component";
import pMinDelay from "p-min-delay";
import PageTitle from "./../../layouts/PageTitle";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

//import Images:
import avatar from "../../../images/avatar/1.png";
import avatar1 from "../../../images/avatar/2.jpg";
import avatar2 from "../../../images/avatar/3.jpg";
import { useAuth } from "../../../context/AuthContext";
import {
  isPaymentAccessOwner,
  usePaymentAccessScope,
} from "../../../hook/usePaymentAccessScope";
import { db, storage } from "../../../firebase";
import {
  query,
  endAt,
  getDocs,
  collection,
  orderBy,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  startAt,
  getCountFromServer,
  where,
  doc,
  deleteDoc,
  updateDoc,
  setDoc,
  collectionGroup,
  Timestamp,
  serverTimestamp,
  getDoc,
  addDoc,
  writeBatch,
  or,
  and,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  getBlob,
  deleteObject,
} from "firebase/storage";
import {
  notifyTopRight,
  notifyWarning,
  notifyError,
} from "../../components/toast/SetToast";
import Toast from "../../components/toast/SetToast";
import { getThaiMonth } from "../Admin/DashboardUtils";
import { te } from "date-fns/locale";
import { create } from "pdf-fontkit";
import { set } from "date-fns";
import {
  toNumber,
  togglePay,
  PaidIcon,
  Phase,
  money,
  parseNum,
  formatDate,
  hasText,
  safeSheetName,
  norm,
  safeExt,
  tsToLocalString,
  dateFromInputYMD,
  getDoctorFrom,
} from "../Admin/DashboardUtils";

const DashboardLoading = () => (
  <React.Fragment>
    <Toast />

    <div className="mb-3 text-center">
      <h2>
        ภาพรวมประจำเดือน{" "}
        <strong>{getThaiMonth(new Date().getMonth())}</strong>
      </h2>
    </div>

    <div
      className="d-flex flex-column align-items-center justify-content-center text-center py-5 my-4"
      aria-live="polite"
    >
      <div
        className="spinner-border text-primary mb-3"
        style={{ width: "3rem", height: "3rem" }}
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
      <h5 className="mb-1">กำลังโหลดข้อมูล Dashboard...</h5>
      <div className="text-muted">
        Please wait while the latest records are loading.
      </div>
    </div>
  </React.Fragment>
);

const getNodeShortName = (nodeName, nodeCodeByName = {}) => {
  const trimmedName = (nodeName || "").trim();
  if (!trimmedName) return "No Node";

  const nodeCode =
    nodeCodeByName[trimmedName] || nodeCodeByName[trimmedName.toLowerCase()];
  if (nodeCode) return nodeCode;

  if (trimmedName === "No Node") return trimmedName;

  return trimmedName
    .replace(/[()]/g, " ")
    .split(/\s+/)
    .filter(
      (word) =>
        word &&
        !["hospital", "center", "clinic", "group", "the", "of", "and"].includes(
          word.toLowerCase(),
        ),
    )
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

const getScheduledNodeSummary = (recordList, nodeCodeByName) => {
  const nodeCounts = {};

  (recordList || []).forEach((activity) => {
    const props = activity.extendedProps || {};
    const nodeName = (props.node || "").trim() || "No Node";
    nodeCounts[nodeName] = (nodeCounts[nodeName] || 0) + 1;
  });

  return Object.keys(nodeCounts)
    .map((node) => ({
      node,
      label: getNodeShortName(node, nodeCodeByName),
      count: nodeCounts[node],
    }))
    .sort((a, b) => b.count - a.count || a.node.localeCompare(b.node));
};

const normalizeHn = (value) => String(value || "").trim();

const getPaymentDate = (value) => {
  const date =
    value && typeof value.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? value
        : value
          ? new Date(value)
          : null;

  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const paymentTimestamp = (value) => {
  const date = getPaymentDate(value);
  return date ? date.getTime() : 0;
};

const formatPaymentDate = (value, includeTime = false) => {
  const date = getPaymentDate(value);
  if (!date) return "-";

  const options = includeTime
    ? {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    : { day: "2-digit", month: "short", year: "numeric" };

  return includeTime
    ? date.toLocaleString("en-GB", options)
    : date.toLocaleDateString("en-GB", options);
};

const timestampToDateInput = (value) => {
  const date = getPaymentDate(value);

  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DoctorDashboard = () => {
  const { currentUser } = useAuth();
  const {
    loading: paymentScopeLoading,
    paymentAccess,
    providerName: scopedDoctorName,
    canManagePayments,
  } = usePaymentAccessScope("sleep_physician");
  const isSuperAdmin = Array.isArray(currentUser && currentUser.roles)
    ? currentUser.roles.includes("super_admin")
    : false;
  // Full payment access sees every provider. A scoped user can still see the
  // dashboard, but its queries are restricted to that user's provider name.
  const canViewOverall = paymentAccess || canManagePayments || isSuperAdmin;

  const [doctorList, setDoctorList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityList, setActivityList] = useState([]);
  const [scoredList, setScoredList] = useState([]);
  const [allActivityList, setAllActivityList] = useState([]);
  const [nodeCodeByName, setNodeCodeByName] = useState({});
  const [selectedDoctor, setSelectedDoctor] = useState("Summary");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [bookingTmpList, setBookingTmpList] = useState([]);
  const [activityTmpList, setActivityTmpList] = useState([]);
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'payments'
  const [payThisRound, setPayThisRound] = useState({});
  const [deduction, setDeduction] = React.useState(0);
  const [adjustmentPay, setAdjustmentPay] = React.useState(0);
  const [payDate, setPayDate] = React.useState(""); // "YYYY-MM-DD"
  const [payNote, setPayNote] = React.useState("");
  const [pendingBatches, setPendingBatches] = useState([]); // list of draft batches for the doctor
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null); // batch doc to review (opens modal/section)
  const [batchEdit, setBatchEdit] = useState(null);
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);
  const [rollingBackBatchKey, setRollingBackBatchKey] = useState("");
  const [readyToEmail, setReadyToEmail] = useState([]);
  const [loadingReady, setLoadingReady] = useState(false);

  const manualOrderRef = useRef("");
  const diagAllRef = React.useRef(null);
  const postAllRef = React.useRef(null);
  const cpapAllRef = React.useRef(null);

  const BATCH_SUBCOL = "batches";
  const doctorKeyFrom = (doctor, selectedDoctor) =>
    (doctor.doctorId || "").trim() || (selectedDoctor || "").trim();

  const scopedDoctor = doctorList.find(
    (doctor) => doctor.name === scopedDoctorName,
  );
  const scopedDoctorKey = scopedDoctor
    ? doctorKeyFrom(scopedDoctor, scopedDoctorName)
    : "";
  const canAccessBatch = (batch) =>
    paymentAccess ||
    (!!scopedDoctorKey && batch && batch.doctorKey === scopedDoctorKey);
  const canRollbackConfirmedBatch =
    paymentAccess && isPaymentAccessOwner(currentUser);

  const PHASE_FIELD_MAP = {
    diagnosis: {
      paidFlag: "isPaidDiagnosis",
      paidDate: "paidDiagnosisDate",
      paidBatch: "paidDiagnosisBatchId",
    },
    postConsult: {
      paidFlag: "isPaidPostConsult",
      paidDate: "paidPostConsultDate",
      paidBatch: "paidPostConsultBatchId",
    },
    cpapFollowup: {
      paidFlag: "isPaidCPAPFollowup",
      paidDate: "paidCPAPFollowupDate",
      paidBatch: "paidCPAPFollowupBatchId",
    },
  };

  const loadPendingBatches = async () => {
    const targetDoctorName = paymentAccess ? selectedDoctor : scopedDoctorName;

    if (!canManagePayments || !targetDoctorName) {
      setPendingBatches([]);
      return;
    }

    if (paymentAccess && targetDoctorName === "Summary") {
      setLoadingPending(true);

      try {
        const results = await Promise.all(
          doctorList.map(async (doctor) => {
            const doctorKey = doctorKeyFrom(doctor, doctor.name);

            const qRef = query(
              collection(db, "paymentBatches", doctorKey, BATCH_SUBCOL),
              where("isConfirmed", "==", false),
              orderBy("createdAt", "desc"),
            );

            const snap = await getDocs(qRef);

            return snap.docs.map((d) => ({
              id: d.id,
              doctorKey,
              ...d.data(),
              refPath: d.ref.path, // keep for direct updates
            }));
          }),
        );

        // flatten
        setPendingBatches(results.flat());
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPending(false);
      }
    } else {
      const doctor = doctorList.find((d) => d.name === targetDoctorName);
      if (!doctor) {
        setPendingBatches([]);
        return;
      }
      const doctorKey = doctorKeyFrom(doctor, targetDoctorName);
      setLoadingPending(true);
      try {
        const qRef = query(
          collection(db, "paymentBatches", doctorKey, BATCH_SUBCOL),
          where("isConfirmed", "==", false),
          orderBy("createdAt", "desc"),
        );
        const snap = await getDocs(qRef);
        const items = snap.docs.map((d) => ({
          id: d.id,
          doctorKey,
          ...d.data(),
          refPath: d.ref.path, // keep for direct updates
        }));
        setPendingBatches(items);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPending(false);
      }
    }
  };

  const loadReadyToEmailForDoctor = async () => {
    if (!paymentAccess) {
      setReadyToEmail([]);
      return;
    }

    if (selectedDoctor === "Summary") {
      setLoadingReady(true);

      try {
        const results = await Promise.all(
          doctorList.map(async (doctor) => {
            const doctorKey = doctorKeyFrom(doctor, doctor.name);

            const qRef = query(
              collection(db, "paymentBatches", doctorKey, BATCH_SUBCOL),
              where("isConfirmed", "==", true),
              // where("isSendEmail", "==", false),
              orderBy("createdAt", "desc"),
            );

            const snap = await getDocs(qRef);

            return snap.docs.map((d) => ({
              id: d.id,
              doctorKey,
              ...d.data(),
              refPath: d.ref.path, // keep for direct updates
            }));
          }),
        );

        // flatten
        setReadyToEmail(results.flat());
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingReady(false);
      }
    } else {
      const doctor = doctorList.find((d) => d.name === selectedDoctor);
      if (!doctor) return;
      const doctorKey = doctorKeyFrom(doctor, selectedDoctor);

      setLoadingReady(true);
      try {
        const qRef = query(
          collection(db, "paymentBatches", doctorKey, "batches"),
          where("isConfirmed", "==", true),
          // where("isSendEmail", "==", false),
          orderBy("createdAt", "desc"),
        );
        const snap = await getDocs(qRef);
        const items = snap.docs.map((d) => ({
          id: d.id,
          doctorKey,
          refPath: d.ref.path, // keep for direct updates
          ...d.data(),
        }));
        setReadyToEmail(items);
      } finally {
        setLoadingReady(false);
      }
    }
  };

  const loadReadyToEmailAll = async () => {
    if (!paymentAccess) {
      setReadyToEmail([]);
      return;
    }

    setLoadingReady(true);
    try {
      const qRef = query(
        collectionGroup(db, "batches"),
        where("isConfirmed", "==", true),
        where("isSendEmail", "==", false),
        orderBy("createdAt", "desc"),
      );
      const snap = await getDocs(qRef);
      const items = snap.docs.map((d) => ({
        id: d.id,
        doctorKey: d.ref.parent.parent.id, // parent doc id under paymentBatches
        refPath: d.ref.path,
        ...d.data(),
      }));
      setReadyToEmail(items);
    } finally {
      setLoadingReady(false);
    }
  };

  const confirmPaymentBatch = async (doctorKey, batchId) => {
    if (!paymentAccess) {
      throw new Error("Only full payment-access users can confirm payments.");
    }

    // fetch the batch doc
    const batchRef = doc(
      db,
      "paymentBatches",
      doctorKey,
      BATCH_SUBCOL,
      batchId,
    );

    const snap = await getDoc(batchRef);
    if (!snap.exists()) throw new Error("Batch not found.");

    const data = snap.data();
    if (data.isConfirmed) throw new Error("Batch already confirmed.");

    const payDateTs =
      data.meta.payDate instanceof Timestamp
        ? data.meta.payDate
        : Timestamp.fromDate(new Date());

    const wb = writeBatch(db);

    // 🔑 1) collect patches PER booking
    const bookingPatchMap = new Map();

    for (const entry of data.entries || []) {
      const { bookingRefPath, phases = [] } = entry || {};
      if (!bookingRefPath || !phases.length) continue;

      const patch = bookingPatchMap.get(bookingRefPath) || {};

      for (const { phase } of phases) {
        const m = PHASE_FIELD_MAP[phase];
        if (!m) {
          console.warn("Unknown phase:", phase, "for booking:", bookingRefPath);
          continue;
        }

        patch[`extendedProps.${m.paidFlag}`] = true;
        patch[`extendedProps.${m.paidDate}`] = payDateTs;
        patch[`extendedProps.${m.paidBatch}`] = batchId;
      }

      bookingPatchMap.set(bookingRefPath, patch);
    }

    // 🔑 2) apply ONE update per booking
    for (const [bookingRefPath, patch] of bookingPatchMap.entries()) {
      if (Object.keys(patch).length === 0) continue;
      const bRef = doc(db, bookingRefPath);
      wb.update(bRef, patch);
    }

    // 🔑 3) update batch doc
    wb.update(batchRef, {
      isConfirmed: true,
      isSendEmail: false,
      confirmedBy: currentUser.displayName,
      confirmedAt: serverTimestamp(),
    });

    await wb.commit();

    notifyTopRight("Payment confirmed.");
    await loadPendingBatches();
    setBatchEdit(null);
    setSelectedBatch(null);
  };

  const returnConfirmedBatchToPending = async (doctorKey, batchId) => {
    if (!canRollbackConfirmedBatch) {
      notifyWarning("Only admin888 can return a confirmed payment to pending.");
      return;
    }

    if (
      !window.confirm(
        "Return this confirmed payment to Pending Payments? This will clear the paid flags linked to this batch so it can be edited and confirmed again.",
      )
    ) {
      return;
    }

    const rollbackKey = `${doctorKey}/${batchId}`;
    setRollingBackBatchKey(rollbackKey);

    try {
      const batchRef = doc(
        db,
        "paymentBatches",
        doctorKey,
        BATCH_SUBCOL,
        batchId,
      );
      const batchSnapshot = await getDoc(batchRef);

      if (!batchSnapshot.exists()) throw new Error("Payment batch not found.");

      const batchData = batchSnapshot.data() || {};
      if (!batchData.isConfirmed) {
        throw new Error("This payment batch is already pending.");
      }

      const linkedBookings = new Map();
      (batchData.entries || []).forEach((entry) => {
        const bookingRefPath = entry && entry.bookingRefPath;
        if (!bookingRefPath) return;

        const linkedBooking = linkedBookings.get(bookingRefPath) || {
          ref: doc(db, bookingRefPath),
          phaseMaps: new Map(),
        };

        (entry.phases || []).forEach((phaseEntry) => {
          const phaseMap = PHASE_FIELD_MAP[phaseEntry && phaseEntry.phase];
          if (phaseMap) linkedBooking.phaseMaps.set(phaseEntry.phase, phaseMap);
        });

        if (linkedBooking.phaseMaps.size) {
          linkedBookings.set(bookingRefPath, linkedBooking);
        }
      });

      if (!linkedBookings.size) {
        throw new Error("This payment batch has no linked booking phases to return.");
      }

      const linkedBookingSnapshots = await Promise.all(
        Array.from(linkedBookings.entries()).map(
          async ([bookingRefPath, linkedBooking]) => ({
            bookingRefPath,
            linkedBooking,
            snapshot: await getDoc(linkedBooking.ref),
          }),
        ),
      );

      const conflicts = [];
      linkedBookingSnapshots.forEach(
        ({ bookingRefPath, linkedBooking, snapshot }) => {
          if (!snapshot.exists()) {
            conflicts.push(bookingRefPath);
            return;
          }

          const props = (snapshot.data() || {}).extendedProps || {};
          linkedBooking.phaseMaps.forEach((phaseMap) => {
            if (props[phaseMap.paidBatch] !== batchId) {
              conflicts.push(bookingRefPath);
            }
          });
        },
      );

      if (conflicts.length) {
        throw new Error(
          "Cannot return this batch because one or more booking phases no longer point to it.",
        );
      }

      const write = writeBatch(db);
      linkedBookings.forEach((linkedBooking) => {
        const patch = {};
        linkedBooking.phaseMaps.forEach((phaseMap) => {
          patch[`extendedProps.${phaseMap.paidFlag}`] = false;
          patch[`extendedProps.${phaseMap.paidDate}`] = null;
          patch[`extendedProps.${phaseMap.paidBatch}`] = null;
        });
        write.update(linkedBooking.ref, patch);
      });

      write.update(batchRef, {
        isConfirmed: false,
        isSendEmail: false,
        confirmedBy: null,
        confirmedAt: null,
      });

      await write.commit();

      setReadyToEmail((current) =>
        current.filter(
          (batch) => !(batch.id === batchId && batch.doctorKey === doctorKey),
        ),
      );
      if (
        selectedBatch &&
        selectedBatch.id === batchId &&
        selectedBatch.doctorKey === doctorKey
      ) {
        setSelectedBatch(null);
      }

      try {
        await Promise.all([loadPendingBatches(), loadReadyToEmailForDoctor()]);
      } catch (refreshError) {
        console.error("Unable to refresh payment batches after rollback", refreshError);
      }
      notifyTopRight("Confirmed payment returned to Pending Payments.");
    } catch (error) {
      console.error("Unable to return confirmed payment to pending", error);
      notifyError("Return to pending failed", error.message || error);
    } finally {
      setRollingBackBatchKey("");
    }
  };

  const openBatchForReview = (batch) => {
    if (!canAccessBatch(batch)) return;
    setBatchEdit(null);
    setSelectedBatch(batch);
  };

  const startBatchEdit = (batch) => {
    if (!canManagePayments || !canAccessBatch(batch) || !batch || batch.isConfirmed)
      return;

    const meta = batch.meta || {};
    setSelectedBatch(batch);
    setBatchEdit({
      id: batch.id,
      doctorKey: batch.doctorKey,
      entries: (batch.entries || []).map((entry) => ({
        ...entry,
        phases: (entry.phases || []).map((phase) => ({ ...phase })),
      })),
      deduction: String(meta.deduction || 0),
      adjustmentPay: String(meta.adjustmentPay || 0),
      payDate: timestampToDateInput(meta.payDate),
      payNote: meta.payNote || "",
    });
  };

  const updateBatchEditPhaseAmount = (entryIndex, phaseIndex, value) => {
    setBatchEdit((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        entries: previous.entries.map((entry, currentEntryIndex) => {
          if (currentEntryIndex !== entryIndex) return entry;

          return {
            ...entry,
            phases: (entry.phases || []).map((phase, currentPhaseIndex) =>
              currentPhaseIndex === phaseIndex
                ? { ...phase, amount: value }
                : phase,
            ),
          };
        }),
      };
    });
  };

  const getBatchEditTotals = (edit) => {
    const amount = (edit.entries || []).reduce(
      (entryTotal, entry) =>
        entryTotal +
        (entry.phases || []).reduce(
          (phaseTotal, phase) =>
            phaseTotal + Math.max(0, toNumber(phase.amount)),
          0,
        ),
      0,
    );
    const wht = amount * 0.03;
    const deductionAmount = Math.max(0, toNumber(edit.deduction));
    const adjustmentAmount = Math.max(0, toNumber(edit.adjustmentPay));

    return {
      amount,
      wht,
      deductionAmount,
      adjustmentAmount,
      totalPay: Math.max(0, amount - wht - deductionAmount + adjustmentAmount),
    };
  };

  const saveBatchEdit = async () => {
    if (!selectedBatch || !batchEdit || batchEdit.id !== selectedBatch.id)
      return;
    if (!canManagePayments || !canAccessBatch(selectedBatch)) {
      notifyError("Update payment failed", "You do not have access to this draft.");
      return;
    }

    if (!batchEdit.payDate) {
      notifyWarning("Please choose a pay date.");
      return;
    }

    const entries = (batchEdit.entries || []).map((entry) => ({
      ...entry,
      phases: (entry.phases || []).map((phase) => ({
        ...phase,
        amount: Math.max(0, toNumber(phase.amount)),
      })),
    }));

    if (!entries.length || !entries.some((entry) => entry.phases.length)) {
      notifyWarning("A payment batch needs at least one payment phase.");
      return;
    }

    const totals = getBatchEditTotals({ ...batchEdit, entries });
    const batchRef = doc(
      db,
      "paymentBatches",
      batchEdit.doctorKey,
      BATCH_SUBCOL,
      batchEdit.id,
    );

    setSavingBatchEdit(true);
    try {
      const snapshot = await getDoc(batchRef);
      if (!snapshot.exists()) throw new Error("Batch not found.");
      if (snapshot.data().isConfirmed)
        throw new Error("Confirmed batches cannot be edited.");

      await updateDoc(batchRef, {
        entries,
        "meta.amount": totals.amount,
        "meta.wht": totals.wht,
        "meta.deduction": totals.deductionAmount,
        "meta.adjustmentPay": totals.adjustmentAmount,
        "meta.totalPay": totals.totalPay,
        "meta.payNote": batchEdit.payNote.trim(),
        "meta.payDate": Timestamp.fromDate(
          new Date(`${batchEdit.payDate}T00:00:00`),
        ),
      });

      const updatedBatch = {
        ...selectedBatch,
        entries,
        meta: {
          ...(selectedBatch.meta || {}),
          amount: totals.amount,
          wht: totals.wht,
          deduction: totals.deductionAmount,
          adjustmentPay: totals.adjustmentAmount,
          totalPay: totals.totalPay,
          payNote: batchEdit.payNote.trim(),
          payDate: Timestamp.fromDate(new Date(`${batchEdit.payDate}T00:00:00`)),
        },
      };

      setSelectedBatch(updatedBatch);
      setPendingBatches((previous) =>
        previous.map((batch) =>
          batch.id === updatedBatch.id ? updatedBatch : batch,
        ),
      );
      setBatchEdit(null);
      await loadPendingBatches();
      notifyTopRight("Draft payment updated.");
    } catch (error) {
      console.error("Update payment draft failed", error);
      notifyError("Update payment failed", error.message || error);
    } finally {
      setSavingBatchEdit(false);
    }
  };

  const editingSelectedBatch =
    !!selectedBatch && !!batchEdit && batchEdit.id === selectedBatch.id;
  const batchEditTotals = editingSelectedBatch
    ? getBatchEditTotals(batchEdit)
    : null;

  const addBookingByOrder = async () => {
    if (!canManagePayments) {
      notifyWarning("Payment access is not configured for this account.");
      return;
    }

    console.log("Adding booking for order:", manualOrderRef.current);
    const order = (manualOrderRef.current || "").trim();
    if (!order) {
      notifyWarning("Please enter an order ID.");
      return;
    }

    // prevent duplicates in current list
    const exists = bookingTmpList.some(
      (b) => (b.extendedProps || {}).order === order,
    );
    console.log("Exists check for order:", order, exists);
    if (exists) {
      notifyWarning(`Order ${order} is already in the list.`);
      return;
    }
    console.log("Searching booking for order:", order);

    try {
      const q = paymentAccess
        ? query(
            collectionGroup(db, "bookings"),
            where("extendedProps.order", "==", order),
          )
        : query(
            collectionGroup(db, "bookings"),
            and(
              where("extendedProps.order", "==", order),
              or(
                where("extendedProps.doctor", "==", scopedDoctorName),
                where("extendedProps.postDoctor", "==", scopedDoctorName),
              ),
            ),
          );
      const snap = await getDocs(q);

      if (snap.empty) {
        notifyWarning(`No booking found for order: ${order}`);
        return;
      }

      // If multiple, take the first. Restricted users only queried their own
      // booking fields above.
      const d = snap.docs[0];
      const booking = { refPath: d.ref.path, id: d.id, ...d.data() };
      // console.log("Found booking:", booking);
      var error = null;
      // // Optional: if you only want bookings for the selected doctor, enforce here:
      // if (
      //   selectedDoctor &&
      //   selectedDoctor !== "Summary" &&
      //   ((booking.extendedProps || {}).doctor || "") !== selectedDoctor
      // ) {
      //   error = "error";
      //   notifyWarning(
      //     `Order ${order} belongs to doctor "${
      //       (booking.extendedProps || {}).doctor || "-"
      //     }", not "${selectedDoctor}".`
      //   );
      //   // return; // uncomment to block adding if doctor mismatch
      // }
      const ep = booking.extendedProps || {};
      const doctor = (ep.doctor || "").trim();
      const postDoctor = (ep.postDoctor || "").trim();

      if (
        selectedDoctor &&
        selectedDoctor !== "Summary" &&
        doctor !== selectedDoctor &&
        postDoctor !== selectedDoctor
      ) {
        error = "error";

        // show what it IS assigned to (both fields if present)
        const current =
          doctor && postDoctor
            ? `${doctor} (postDoctor: ${postDoctor})`
            : doctor || postDoctor || "-";

        notifyWarning(
          `Order ${order} belongs to doctor "${current}", not "${selectedDoctor}".`,
        );

        return;
      }

      // Keep Activity column aligned; put a blank or derived timestamp
      setActivityTmpList((prev) => [
        ...prev,
        {
          ScoredAt: "-", // or tsToLocalString(booking.extendedProps.createdAt)
        },
      ]);

      // Add to the table list
      setBookingTmpList((prev) => [...prev, booking]);

      manualOrderRef.current = "";
      if (!error) {
        notifyTopRight(`Added order ${order}.`);
      }
    } catch (err) {
      console.error(err);
      notifyError("Failed to add booking by order.", err);
    }
  };

  const getRate = (doctor, bookingRow, phase) => {
    if (!doctor) return 0;

    const ep = bookingRow.extendedProps || {};
    const isCash = ep.healthInsurance === "ชำระเงินเอง";

    const fields = {
      diagnosis: isCash ? "diagnosisCashRate" : "diagnosisSocialSecurityRate",
      post: isCash ? "postConsultCashRate" : "postConsultSocialSecurityRate",
      cpap: isCash ? "cpapFollowupCashRate" : "cpapFollowupSocialSecurityRate",
    };

    // ✅ Base key
    const key = fields[phase];
    let rate = parseNum(doctor[key]);

    // ✅ Special override rule
    if (
      doctor.name === "T.Chatkarin" &&
      ep.node === "Pat Rangsit Hospital Group" &&
      phase === "diagnosis"
    ) {
      rate = isCash ? 2061.86 : 2061.86;
    }

    return rate;
  };

  const removeBooking = (order) => {
    setActivityTmpList((prev) => prev.filter((row) => row.Order !== order));

    setBookingTmpList((prev) =>
      prev.filter((row) => row.extendedProps.order !== order),
    );
    // also remove from payThisRound selection
    setPayThisRound((prev) => {
      const updated = { ...prev };
      delete updated[order];
      return updated;
    });
  };

  // Refs to set indeterminate state on header checkboxes
  // Is a row eligible to be selected this round (not already paid)?
  const eligible = (row, phase) => {
    const ep = row.extendedProps || {};
    if (phase === "diagnosis") return !ep.isPaidDiagnosis;
    if (phase === "post") return !ep.isPaidPostConsult;
    if (phase === "cpap") return !ep.isPaidCPAPFollowup;
    return false;
  };

  // Compute header checkbox states (checked / indeterminate)
  const headerState = React.useMemo(() => {
    const phases = ["diagnosis", "post", "cpap"];
    const out = {};
    phases.forEach((phase) => {
      const eligOrders = bookingTmpList
        .filter((row) => eligible(row, phase))
        .map((row) => (row.extendedProps || {}).order)
        .filter(Boolean);

      if (eligOrders.length === 0) {
        out[phase] = { checked: false, indeterminate: false, disabled: true };
        return;
      }

      let selectedCount = 0;
      eligOrders.forEach((ord) => {
        const sel = (payThisRound[ord] || {})[phase];
        if (sel) selectedCount++;
      });

      out[phase] = {
        checked: selectedCount === eligOrders.length,
        indeterminate: selectedCount > 0 && selectedCount < eligOrders.length,
        disabled: false,
      };
    });
    return out;
  }, [bookingTmpList, payThisRound]);

  // Keep the DOM "indeterminate" visual in sync
  React.useEffect(() => {
    if (diagAllRef.current)
      diagAllRef.current.indeterminate =
        headerState.diagnosis.indeterminate || false;
    if (postAllRef.current)
      postAllRef.current.indeterminate =
        headerState.post.indeterminate || false;
    if (cpapAllRef.current)
      cpapAllRef.current.indeterminate =
        headerState.cpap.indeterminate || false;
  }, [headerState]);

  // Toggle all for a phase
  const toggleAllPhase = (phase) => (e) => {
    const checked = e.target.checked;
    setPayThisRound((prev) => {
      const next = { ...prev };
      bookingTmpList.forEach((row) => {
        if (!eligible(row, phase)) return; // skip already-paid rows
        const ord = (row.extendedProps || {}).order;
        if (!ord) return;
        const cur = next[ord] || { diagnosis: false, post: false, cpap: false };
        next[ord] = { ...cur, [phase]: checked };
      });
      return next;
    });
  };

  useEffect(() => {
    if (paymentScopeLoading) return undefined;

    const fetchData = async () => {
      setLoading(true);

      const fetchDoctorList = async () => {
        const q = paymentAccess
          ? query(
              collection(db, "doctors"),
              //   orderBy("fullname", "desc")
            )
          : query(
              collection(db, "doctors"),
              where("name", "==", scopedDoctorName),
            );
        const querySnapshot = await getDocs(q);
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        data.sort((a, b) =>
          String(a.fullnameEn || a.name || "").localeCompare(
            String(b.fullnameEn || b.name || ""),
          ),
        );
        setDoctorList(data);
      };

      const fetchNodeCodeByName = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, "nodes"));
          const data = {};

          querySnapshot.forEach((doc) => {
            const node = doc.data() || {};
            const nodeName = (node.nodename || "").trim();
            const nodeCode = (node.nodecode || "").trim();
            if (nodeName && nodeCode) {
              data[nodeName] = nodeCode;
              data[nodeName.toLowerCase()] = nodeCode;
            }
          });

          setNodeCodeByName(data);
        } catch (err) {
          console.warn("Unable to load node codes for dashboard badges", err);
          setNodeCodeByName({});
        }
      };

      const fetchActivityList = async () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        /////////////////////////////////////////////////////////////////////////////////////
        // Get string date for query booking in this month
        const startDate = new Date(year, month, 1, 0, 0, 0);
        const formattedStart = formatDate(startDate);

        const endDate = new Date(year, month + 1, 0, 23, 59, 59);
        const formattedEnd = formatDate(endDate);

        if (!paymentAccess) {
          if (!scopedDoctorName) {
            setActivityList([]);
            setScoredList([]);
            setAllActivityList([]);
            return;
          }

          // Restricted users load only bookings that identify their configured
          // doctor name. This keeps the overview from fetching other doctors'
          // cases before it renders the cards.
          const bookingsRef = collectionGroup(db, "bookings");
          const [doctorSnapshot, postDoctorSnapshot] = await Promise.all([
            getDocs(
              query(
                bookingsRef,
                where("extendedProps.doctor", "==", scopedDoctorName),
              ),
            ),
            getDocs(
              query(
                bookingsRef,
                where("extendedProps.postDoctor", "==", scopedDoctorName),
              ),
            ),
          ]);
          const bookingByPath = new Map();
          [doctorSnapshot, postDoctorSnapshot].forEach((snapshot) => {
            snapshot.forEach((bookingDoc) => {
              bookingByPath.set(bookingDoc.ref.path, {
                id: bookingDoc.id,
                ...bookingDoc.data(),
              });
            });
          });
          const ownBookings = Array.from(bookingByPath.values());
          const timestampToMillis = (value) => {
            if (value && typeof value.toMillis === "function")
              return value.toMillis();
            if (value instanceof Date) return value.getTime();
            return null;
          };

          const data = ownBookings
            .filter((booking) => {
              const bookingDate =
                (booking.extendedProps || {}).latestSleepStudyDate || "";
              return bookingDate >= formattedStart && bookingDate <= formattedEnd;
            })
            .sort((a, b) =>
              String(
                (b.extendedProps || {}).latestSleepStudyDate || "",
              ).localeCompare(
                String((a.extendedProps || {}).latestSleepStudyDate || ""),
              ),
            );
          setActivityList(data);

          const startInDate = Timestamp.fromDate(
            new Date(year, month, 1, 0, 0, 0),
          );
          const endInDate = Timestamp.fromDate(
            new Date(year, month + 1, 0, 23, 59, 59),
          );
          const ownOrders = [
            ...new Set(
              ownBookings
                .map((booking) => (booking.extendedProps || {}).order)
                .filter(Boolean),
            ),
          ];
          const activitySnapshots = await Promise.all(
            Array.from(
              { length: Math.ceil(ownOrders.length / 10) },
              (_, index) => ownOrders.slice(index * 10, index * 10 + 10),
            ).map((orderChunk) =>
              getDocs(
                query(
                  collection(db, "activities"),
                  where("order", "in", orderChunk),
                ),
              ),
            ),
          );
          const startMs = startInDate.toMillis();
          const endMs = endInDate.toMillis();
          const data2 = [];
          activitySnapshots.forEach((snapshot) => {
            snapshot.forEach((activityDoc) => {
              const activity = { id: activityDoc.id, ...activityDoc.data() };
              const activityMs = timestampToMillis(activity.startDate);
              if (
                activity.activity === "Doctor Scored" &&
                activityMs !== null &&
                activityMs >= startMs &&
                activityMs <= endMs
              ) {
                data2.push(activity);
              }
            });
          });
          setScoredList(data2);

          const prevMonth = new Date(year, month - 1, 1);
          const startDatePrevMonth = Timestamp.fromDate(
            new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1, 0, 0, 0),
          );
          const endDateCurrentMonth = Timestamp.fromDate(
            new Date(year, month + 1, 0, 23, 59, 59),
          );
          const rangeStartMs = startDatePrevMonth.toMillis();
          const rangeEndMs = endDateCurrentMonth.toMillis();
          setAllActivityList(
            ownBookings.filter((booking) => {
              const bookingStartMs = timestampToMillis(booking.start);
              return (
                bookingStartMs !== null &&
                bookingStartMs >= rangeStartMs &&
                bookingStartMs <= rangeEndMs
              );
            }),
          );
          return;
        }

        const q = query(
          collectionGroup(db, "bookings"),
          where("extendedProps.latestSleepStudyDate", ">=", formattedStart),
          where("extendedProps.latestSleepStudyDate", "<=", formattedEnd),
          orderBy("extendedProps.latestSleepStudyDate", "desc"),
        );

        /////////////////////////////////////////////////////////////////////////////////////
        // Get date for query activities status Technician Scored in this month
        const startInDate = Timestamp.fromDate(
          new Date(year, month, 1, 0, 0, 0),
        );

        const endInDate = Timestamp.fromDate(
          new Date(year, month + 1, 0, 23, 59, 59),
        ); // day=0 means last day of previous month

        const q2 = query(
          collection(db, "activities"),
          where("activity", "==", "Doctor Scored"),
          where("startDate", ">=", startInDate),
          where("startDate", "<=", endInDate),
        );
        //   collection(db, "activities"),
        //   where("title", "==", "booking"),
        //   where("createdAt", ">=", startInDate),
        //   where("createdAt", "<=", endInDate),
        //   where("activity", "==", "Technician Scored")
        //   orderBy("createdAt", "desc")

        /////////////////////////////////////////////////////////////////////////////////////
        // Get date for query all booking in previous month to current month
        const prevMonth = new Date(year, month - 1, 1);
        const startDatePrevMonth = Timestamp.fromDate(
          new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1, 0, 0, 0),
        );
        console.log("Start Date Prev Month: ", startDatePrevMonth.toDate());

        // End of current month
        const endDateCurrentMonth = Timestamp.fromDate(
          new Date(year, month + 1, 0, 23, 59, 59),
        );
        console.log("End Date Prev Month: ", endDateCurrentMonth.toDate());

        const q3 = query(
          collectionGroup(db, "bookings"),
          where("start", ">=", startDatePrevMonth),
          where("start", "<=", endDateCurrentMonth),
          //   where("extendedProps.status", "not-in", ["Cancel","Failure"]),
        );

        /////////////////////////////////////////////////////////////////////////////////////

        const [querySnapshot, querySnapshot2, querySnapshot3] =
          await Promise.all([getDocs(q), getDocs(q2), getDocs(q3)]);

        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        setActivityList(data);
        console.log("data: ", data[0]);

        const data2 = [];
        querySnapshot2.forEach((doc) => {
          data2.push({ id: doc.id, ...doc.data() });
        });
        const uniqueData2 = data2.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.order === item.order),
        );
        // console.log("Scored List: ", uniqueData2);
        setScoredList(data2);
        console.log("data2: ", data2[0]);

        const data3 = [];
        querySnapshot3.forEach((doc) => {
          data3.push({ id: doc.id, ...doc.data() });
        });
        // console.log("All Activity List: ", data3);
        setAllActivityList(data3);
        console.log("data3: ", data3[0]);
      };
      try {
        if (!paymentAccess && !scopedDoctorName) {
          setDoctorList([]);
          setActivityList([]);
          setScoredList([]);
          setAllActivityList([]);
          setNodeCodeByName({});
          return;
        }

        if (!canViewOverall) {
          if (!scopedDoctorName) {
            setDoctorList([]);
          } else {
            await fetchDoctorList();
          }
          setActivityList([]);
          setScoredList([]);
          setAllActivityList([]);
          setNodeCodeByName({});
          return;
        }

        await Promise.all([
          fetchActivityList(),
          fetchDoctorList(),
          fetchNodeCodeByName(),
        ]);
      } catch (err) {
        console.error("Error fetching data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return undefined;
  }, [canViewOverall, paymentAccess, paymentScopeLoading, scopedDoctorName]);

  useEffect(() => {
    if (paymentScopeLoading) return;

    setSelectedDoctor((current) =>
      paymentAccess ? current || "Summary" : scopedDoctorName || "Summary",
    );
    if (!paymentAccess) {
      setActiveTab(canViewOverall ? "overview" : "payments");
      setBookingTmpList([]);
      setActivityTmpList([]);
      setPayThisRound({});
      setSelectedBatch(null);
      setBatchEdit(null);
    }
  }, [canViewOverall, paymentAccess, paymentScopeLoading, scopedDoctorName]);

  // Example: trigger on tab switch to “Pending Payments”
  useEffect(() => {
    // if you have a selectedTab state, call only when that tab active
    if (
      canManagePayments &&
      (paymentAccess ? selectedDoctor : scopedDoctorName) &&
      /* selectedTab === 'pending' */ true
    ) {
      loadPendingBatches();
      loadReadyToEmailForDoctor();
    }
  }, [
    canManagePayments,
    doctorList,
    paymentAccess,
    scopedDoctorName,
    selectedDoctor,
  ]);

  useEffect(() => {
    setPayThisRound((prev) => {
      const next = { ...prev };
      const orders = new Set(
        (bookingTmpList || [])
          .map((r) => (r.extendedProps || {}).order)
          .filter(Boolean),
      );

      // add missing orders with defaults
      orders.forEach((ord) => {
        if (!next[ord])
          next[ord] = { diagnosis: false, post: false, cpap: false };
      });

      // remove entries that are no longer in the table (optional)
      Object.keys(next).forEach((ord) => {
        if (!orders.has(ord)) delete next[ord];
      });

      return next;
    });
  }, [bookingTmpList]);

  const SelectDoctor = (doctor) => {
    if (!paymentAccess) return;
    setSelectedDoctor(doctor);
    console.log("Selected doctor: ", doctor);
  };

  const excludeStatus = ["Cancel"];
  const doctorsSortedByRecord = doctorList
    .map((doctor) => {
      const recordList = activityList.filter(
        (activity) =>
          activity.extendedProps.doctor === doctor.name &&
          !excludeStatus.includes(activity.extendedProps.status),
      );
      return { ...doctor, recordListLength: recordList.length };
    })
    .sort((a, b) => b.recordListLength - a.recordListLength);

  const exportActivityData = (
    recordedList,
    matchedScoredBookings,
    selectedDoctor,
    startDateStr,
    endDateStr,
  ) => {
    const toRow = (item) => {
      const props = item.extendedProps || {};
      return {
        Order: props.order || "",
        Firstname: props.firstname || "",
        Lastname: props.lastname || "",
        HN: props.hn || "",
        HospitalNumber: props.hospitalNumber || "",
        Gender: props.gender || "",
        NationalID: props.nationalId || "",
        TechRecord: props.techrecord || "",
        TechScore: props.technician || "",
        Doctor: props.doctor || "",
        SleepStudyDate: props.latestSleepStudyDate || "",
        Status: props.status || "",
        Phone: props.phone || "",
        Customer: props.customer || "",
        Node: props.node || "",
        AHI: props.ahi || "",
        Note: props.note || "",
      };
    };

    const data1 = recordedList.map(toRow);
    const data2 = matchedScoredBookings.map(toRow);
    const headers = Object.keys(data1[0] || {});

    const title = `Sleep Study Report: ${startDateStr} to ${endDateStr} (Doctor: ${selectedDoctor})`;

    const sheetData = [];

    // Title row
    sheetData.push([title]);
    sheetData.push([]);

    // Recorded List section
    sheetData.push(["Recorded List"]);
    sheetData.push(headers);
    sheetData.push(...data1.map((item) => headers.map((h) => item[h])));
    sheetData.push([`Total: ${data1.length} items`]);

    // 5 empty rows
    for (let i = 0; i < 3; i++) sheetData.push([]);

    // Matched Scored Bookings section
    sheetData.push(["Matched Scored Bookings"]);
    sheetData.push(headers);
    sheetData.push(...data2.map((item) => headers.map((h) => item[h])));
    sheetData.push([`Total: ${data2.length} items`]);

    // Create and export workbook
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Activity Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "Activity_Report.xlsx",
    );
  };

  const ExportSummaryCountExcel = (summary, startDate, endDate) => {
    const ws = XLSX.utils.json_to_sheet(summary);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");

    const fileName = `Doctor_Summary_${startDate}_to_${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const makeRow = (a, b) => {
    const ext = safeExt(b);
    const bb = b || {};
    return {
      ScoredAt: tsToLocalString(a.startDate || null), // first column
      Order: a.order || ext.order || "",
      Firstname: ext.firstname || bb.firstname || "",
      Lastname: ext.lastname || bb.lastname || "",
      HN: ext.hn || bb.hn || "",
      HospitalNumber: ext.hospitalNumber || bb.hospitalNumber || "",
      Gender: ext.gender || bb.gender || "",
      NationalID: ext.nationalId || bb.nationalId || "",
      TechRecord: ext.techrecord || "",
      TechScore: ext.technician || "",
      Doctor: ext.doctor || bb.doctor || "",
      SleepStudyDate: ext.latestSleepStudyDate || bb.latestSleepStudyDate || "",
      Status: ext.status || bb.status || "",
      Phone: ext.phone || bb.phone || "",
      Customer: ext.customer || bb.customer || "",
      Node: ext.node || bb.node || "",
      AHI: ext.ahi || bb.ahi || "",
      Note: ext.note || bb.note || "",
    };
  };

  const handleDeleteBatch = async (batchId) => {
    if (!paymentAccess) {
      notifyWarning("Only full payment-access users can delete payment drafts.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this draft payment?"))
      return;

    console.log(selectedDoctor);
    console.log("Deleting batch:", batchId);

    const doctorInfo = doctorList.find((doc) => doc.name === selectedDoctor);
    if (!doctorInfo) return;

    const doctorKey = doctorInfo.doctorId || doctorInfo.id || doctorInfo.name;

    try {
      await deleteDoc(doc(db, "paymentBatches", doctorKey, "batches", batchId));
      notifyTopRight("Draft payment deleted");

      // Refresh list or remove from state
      setPendingBatches((prev) => prev.filter((b) => b.id !== batchId));
      if (selectedBatch && selectedBatch.id === batchId) {
        setBatchEdit(null);
        setSelectedBatch(null);
      }
    } catch (err) {
      console.error("Delete failed", err);
      notifyError("Delete failed", err.message || err);
    }
  };

  const Export = async () => {
    try {
      if (!startDate || !endDate) {
        alert("Please pick Start and End dates.");
        return;
      }

      setLoading(true);
      const startTs = Timestamp.fromDate(
        dateFromInputYMD(startDate, 0, 0, 0, 0),
      );
      const endTs = Timestamp.fromDate(
        dateFromInputYMD(endDate, 23, 59, 59, 999),
      );

      const selectedProviderName = paymentAccess
        ? selectedDoctor
        : scopedDoctorName;
      let activities = [];
      let bookings = [];

      if (paymentAccess) {
        // Full-access users may query all activity records for the selected
        // date range, then optionally narrow them to a selected doctor below.
        const activitiesSnap = await getDocs(
          query(
            collectionGroup(db, "activities"),
            where("activity", "in", ["Doctor Scored", "Post-Consult"]),
            where("startDate", ">=", startTs),
            where("startDate", "<=", endTs),
          ),
        );
        activities = activitiesSnap.docs.map((activityDoc) => {
          const activity = activityDoc.data() || {};
          return {
            order: activity.order || null,
            hn: activity.hn || null,
            detail2: activity.detail2 || null,
            startDate: activity.startDate || null,
          };
        });
      } else {
        // Restricted users fetch only bookings assigned to their configured
        // booking name before looking up activities for those order IDs.
        const ownBookingsSnapshot = await getDocs(
          query(
            collectionGroup(db, "bookings"),
            or(
              where("extendedProps.doctor", "==", selectedProviderName),
              where("extendedProps.postDoctor", "==", selectedProviderName),
            ),
          ),
        );
        bookings = ownBookingsSnapshot.docs.map((bookingDoc) => ({
          refPath: bookingDoc.ref.path,
          ...bookingDoc.data(),
        }));

        const ownOrderList = [
          ...new Set(
            bookings
              .map((booking) => (booking.extendedProps || {}).order)
              .filter(hasText),
          ),
        ];
        const ownActivityChunks = [];
        for (let i = 0; i < ownOrderList.length; i += 10) {
          ownActivityChunks.push(ownOrderList.slice(i, i + 10));
        }
        const activitySnapshots = await Promise.all(
          ownActivityChunks.map((orderChunk) =>
            getDocs(
              query(
                collectionGroup(db, "activities"),
                where("order", "in", orderChunk),
                where("startDate", ">=", startTs),
                where("startDate", "<=", endTs),
              ),
            ),
          ),
        );
        activitySnapshots.forEach((activitySnapshot) => {
          activitySnapshot.docs.forEach((activityDoc) => {
            const activity = activityDoc.data() || {};
            if (
              !["Doctor Scored", "Post-Consult"].includes(activity.activity)
            ) {
              return;
            }
            activities.push({
              order: activity.order || null,
              hn: activity.hn || null,
              detail2: activity.detail2 || null,
              startDate: activity.startDate || null,
            });
          });
        });
      }

      if (!activities.length) {
        alert("No 'Doctor Scored' activities found in this range.");
        return;
      }

      setBookingTmpList([]);
      setActivityTmpList([]);

      // 2) DEDUPE by order: keep the latest createdAt per order
      // DEDUPE by order: first wins
      const byOrder = new Map();

      for (let i = 0; i < activities.length; i++) {
        const a = activities[i];
        if (!hasText(a.order)) continue;
        if (!byOrder.has(a.order)) byOrder.set(a.order, a);
      }

      const uniqueActivities = Array.from(byOrder.values());

      // 3) distinct orders from unique activities
      const orderList = [
        ...new Set(uniqueActivities.map((a) => a.order).filter(hasText)),
      ];
      if (!orderList.length) {
        alert("No valid order IDs found for matched activities.");
        return;
      }

      // 4) Full-access users now fetch the matching bookings. Restricted users
      // already fetched only their own bookings above and never query another
      // provider's booking records.
      const CHUNK = 10;
      if (paymentAccess) {
        for (let i = 0; i < orderList.length; i += CHUNK) {
          const slice = orderList.slice(i, i + CHUNK);
          const base = where("extendedProps.order", "in", slice);
          const bookingQuery =
            selectedProviderName && selectedProviderName !== "Summary"
              ? query(
                  collectionGroup(db, "bookings"),
                  and(
                    base,
                    or(
                      where("extendedProps.doctor", "==", selectedProviderName),
                      where("extendedProps.postDoctor", "==", selectedProviderName),
                    ),
                  ),
                )
              : query(collectionGroup(db, "bookings"), base);
          const bookingSnapshot = await getDocs(bookingQuery);
          bookings.push(
            ...bookingSnapshot.docs.map((bookingDoc) => ({
              refPath: bookingDoc.ref.path,
              ...bookingDoc.data(),
            })),
          );
        }

        if (selectedProviderName && selectedProviderName !== "Summary") {
          const wantedDoctorName = norm(selectedProviderName);
          bookings = bookings.filter(
            (booking) => norm(getDoctorFrom(booking)) === wantedDoctorName,
          );
        }
      } else {
        bookings = bookings.filter((booking) =>
          orderList.includes((booking.extendedProps || {}).order),
        );
      }

      // 5) index bookings by order (strict join)
      const bookingByOrder = new Map();
      for (const b of bookings) {
        const ord = b.extendedProps.order;
        if (hasText(ord) && !bookingByOrder.has(ord))
          bookingByOrder.set(ord, b);
      }

      // 6) workbook
      const wb = XLSX.utils.book_new();
      console.log("unique: ", uniqueActivities);

      if (selectedProviderName && selectedProviderName !== "Summary") {
        // single doctor: one row per unique order (matched only)
        const rows = uniqueActivities
          .filter((a) => hasText(a.order))
          .map((a) => ({ a, b: bookingByOrder.get(a.order) || null }))
          .filter(({ b }) => !!b)
          .map(({ a, b }) => makeRow(a, b));
        console.log("Export rows:", rows);
        setActivityTmpList(rows);

        // 2. Build an index from rows.Order
        const orderIndex = new Map();
        rows.forEach((r, i) => {
          if (hasText(r.Order)) orderIndex.set(r.Order, i);
        });

        // 3. Convert bookingByOrder to array
        const arr = Array.from(bookingByOrder.values());

        // 4. Sort booking arr by the index
        arr.sort((b1, b2) => {
          const o1 = orderIndex.get(b1.extendedProps.order);
          const o2 = orderIndex.get(b2.extendedProps.order);
          return (
            (o1 !== undefined ? o1 : Infinity) -
            (o2 !== undefined ? o2 : Infinity)
          );
        });

        setBookingTmpList(arr);
        console.log("Sorted Arr Booking:", arr);

        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(
          wb,
          ws,
          safeSheetName(selectedProviderName),
        );
      } else {
        // summary: one sheet per doctor; plus "(Unmatched)" for unique orders with no booking
        const perDoctor = {};
        const unmatched = [];

        for (const a of uniqueActivities) {
          if (!hasText(a.order)) {
            unmatched.push(makeRow(a, null));
            continue;
          }
          const b = bookingByOrder.get(a.order) || null;
          if (!b) {
            unmatched.push(makeRow(a, null));
            continue;
          }

          const docName = getDoctorFrom(b) || "(Unknown Doctor)";
          const key = docName.trim() || "(Unknown Doctor)";
          if (!perDoctor[key]) perDoctor[key] = [];
          perDoctor[key].push(makeRow(a, b));
        }

        Object.keys(perDoctor).forEach((doc) => {
          const ws = XLSX.utils.json_to_sheet(perDoctor[doc]);
          XLSX.utils.book_append_sheet(wb, ws, safeSheetName(doc));
        });
        if (unmatched.length) {
          const wsUn = XLSX.utils.json_to_sheet(unmatched);
          XLSX.utils.book_append_sheet(wb, wsUn, safeSheetName("(Unmatched)"));
        }
      }

      const fileName = `DoctorScored_${startDate}_to_${endDate}${
        selectedProviderName && selectedProviderName !== "Summary"
          ? `_${selectedProviderName}`
          : "_Summary"
      }.xlsx`;
      // XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const fixAllPostConsultPaidFalse = async (opts = { clearMeta: false }) => {
    if (
      !window.confirm(
        `This will set extendedProps.isPaidPostConsult = false on ALL bookings that currently have it true.\n` +
          (opts.clearMeta
            ? `It will ALSO clear paidPostConsultDate and paidPostConsultBatchId.\n`
            : ``) +
          `Proceed?`,
      )
    ) {
      return;
    }

    try {
      // 1) Query all offending bookings via collection group
      const qRef = query(
        collectionGroup(db, "bookings"),
        where("extendedProps.isPaidPostConsult", "==", true),
      );
      const snap = await getDocs(qRef);

      if (snap.empty) {
        notifyTopRight("No bookings found with isPaidPostConsult = true.");
        return;
      }

      // 2) Bulk update in chunks of 500
      let updated = 0;
      let batch = writeBatch(db);
      let ops = 0;

      for (const d of snap.docs) {
        const patch = {
          "extendedProps.isPaidPostConsult": false,
        };
        if (opts.clearMeta) {
          patch["extendedProps.paidPostConsultDate"] = null;
          patch["extendedProps.paidPostConsultBatchId"] = null;
        }

        batch.update(d.ref, patch);
        ops++;
        updated++;

        if (ops === 500) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }

      if (ops > 0) {
        await batch.commit();
      }

      notifyTopRight(`Updated ${updated} booking(s).`);
    } catch (err) {
      console.error(err);
      notifyError("Bulk fix failed", err.message || err);
    }
  };

  // const exportBatchToExcel = (batch, selectedDoctor) => {
  //   if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
  //     notifyWarning("No entries to export.");
  //     return;
  //   }

  //   try {
  //     const rows = batch.entries.map((e, index) => {
  //       const phases = Array.isArray(e.phases) ? e.phases : [];

  //       const diagnosis = phases.find((p) => p.phase === "diagnosis");
  //       const postConsult = phases.find((p) => p.phase === "postConsult");
  //       const cpap = phases.find((p) => p.phase === "cpapFollowup");

  //       console.log("Entry phases:", phases);
  //       console.log("Diagnosis phase:", diagnosis);
  //       console.log("Post-Consult phase:", postConsult);
  //       console.log("CPAP phase:", cpap);

  //       const paid = e.paid || {};
  //       const paidDiag = paid.diagnosis || {};
  //       const paidPost = paid.postConsult || {};
  //       const paidCpap = paid.cpapFollowup || {};

  //       const diagAmt = diagnosis ? Number(diagnosis.amount || 0) : 0;
  //       const postAmt = postConsult ? Number(postConsult.amount || 0) : 0;
  //       const cpapAmt = cpap ? Number(cpap.amount || 0) : 0;

  //       return {
  //         No: index + 1,
  //         Order: e.order || "",
  //         HN: e.hn || "",
  //         Firstname: e.firstname || "",
  //         Lastname: e.lastname || "",
  //         Node: e.node || "",
  //         Customer: e.customer || "",
  //         HealthInsurance: e.healthInsurance || "-",

  //         // Diagnosis group
  //         Diagnosis: diagAmt || "",
  //         // "Diagnosis Paid": paidDiag.isPaid ? "✓" : "",
  //         // "Diagnosis Paid Date":
  //         //   paidDiag.paidDate && paidDiag.paidDate.seconds
  //         //     ? new Date(paidDiag.paidDate.seconds * 1000).toLocaleDateString()
  //         //     : "",

  //         // Post-Consult group
  //         "Post-Consult Amount (THB)": postAmt || "0",

  //         // CPAP Follow-up group
  //         "CPAP Follow-up Amount (THB)": cpapAmt || "0",

  //         // Row total
  //         "Total (THB)": diagAmt + postAmt + cpapAmt || "0",
  //       };
  //     });

  //     const ws = XLSX.utils.json_to_sheet(rows);

  //     ws["!cols"] = [
  //       { wch: 4 }, // No
  //       { wch: 16 }, // Order
  //       { wch: 14 }, // HN
  //       { wch: 16 }, // Firstname
  //       { wch: 16 }, // Lastname
  //       { wch: 24 }, // Node
  //       { wch: 24 }, // Customer
  //       { wch: 16 }, // HealthInsurance

  //       { wch: 18 }, // Diagnosis Amount

  //       { wch: 20 }, // Post Amount

  //       { wch: 22 }, // CPAP Amount

  //       { wch: 14 }, // Total
  //     ];

  //     // Summary sheet
  //     const m = batch.meta || {};
  //     const toDate = (ts) =>
  //       ts && ts.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "";

  //     const toDateOnly = (ts) =>
  //       ts && ts.seconds
  //         ? new Date(ts.seconds * 1000).toLocaleDateString()
  //         : "";

  //     const summaryAoA = [
  //       ["Doctor", selectedDoctor || batch.doctorId || batch.doctorKey || "-"],
  //       ["Month", batch.month || ""],
  //       ["Period Start", batch.period.start || ""],
  //       ["Period End", batch.period.end || ""],
  //       ["Confirmed", batch.isConfirmed ? "Yes" : "No"],
  //       ["Email Sent", batch.isSendEmail ? "Yes" : "No"],
  //       ["Created By", batch.createdBy || ""],
  //       ["Confirmed By", batch.confirmedBy || ""],
  //       ["Created At", toDate(batch.createdAt)],
  //       ["Confirmed At", toDate(batch.confirmedAt)],
  //       [],
  //       ["Amount", m.amount || 0],
  //       ["WHT 3%", -m.wht || 0],
  //       ["Deduction", -m.deduction || 0],
  //       ["Adjustment Pay", +m.adjustmentPay || 0],
  //       ["Total Pay", m.totalPay || 0],
  //       ["Pay Date", toDateOnly(m.payDate)],
  //       ["Note", m.payNote || ""],
  //     ];

  //     const wsMeta = XLSX.utils.aoa_to_sheet(summaryAoA);

  //     const wb = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(wb, ws, "Batch Details");
  //     XLSX.utils.book_append_sheet(wb, wsMeta, "Summary");

  //     const safeDoctor = (
  //       selectedDoctor ||
  //       batch.doctorId ||
  //       batch.doctorKey ||
  //       "Doctor"
  //     ).replace(/[\\\/:*?\[\]]/g, "_");
  //     const fileName = `PaymentBatch_${safeDoctor}_${batch.month || ""}.xlsx`;

  //     XLSX.writeFile(wb, fileName);
  //     notifyTopRight("Exported batch to Excel.");
  //   } catch (err) {
  //     console.error("Export error:", err);
  //     notifyError("Export failed", err.message || err);
  //   }
  // };

  const exportToExcel = (data) => {
    const rows = data.map((d, i) => ({
      No: i + 1,
      Order: d.order,
      Patient: d.patientName,
      HN: d.hn,
      "Payment Batch": d.paymentBatchId,
      "Batch ID": d.batchId,
      "Paid Date": d.paidDate,
      "Booking Ref": d.bookingRefPath,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Diagnosis Mismatch");

    XLSX.writeFile(workbook, "diagnosis_payment_mismatch.xlsx");
  };

  const exportBatchToExcel = (batch, selectedDoctor) => {
    if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
      notifyWarning("No entries to export.");
      return;
    }

    try {
      const doctorName =
        selectedDoctor === "Summary"
          ? (doctorList.find((d) => d.doctorId === batch.doctorKey)
              ? doctorList.find((d) => d.doctorId === batch.doctorKey).name
              : batch.doctorId) || "Doctor"
          : selectedDoctor || batch.doctorId || batch.doctorKey || "Doctor";

      const safeDoctor = doctorName.replace(/[\\\/:*?\[\]]/g, "_");
      const fileName = `PaymentBatch_${safeDoctor}_${batch.month || ""}.xlsx`;

      const bytes = buildPhysicianPaymentBatchWorkbookBytes(batch, doctorName);

      const wb = XLSX.read(bytes, { type: "array" });
      XLSX.writeFile(wb, fileName);

      notifyTopRight("Exported batch to Excel.");
    } catch (err) {
      console.error("Export error:", err);
      notifyError("Export failed", err.message || err);
    }
  };

  const buildPhysicianPaymentBatchWorkbookBytes = (batch, doctorName) => {
    if (!batch || !Array.isArray(batch.entries) || batch.entries.length === 0) {
      throw new Error("No entries to export.");
    }

    const rows = batch.entries.map((e, index) => {
      const phases = Array.isArray(e.phases) ? e.phases : [];
      const diagnosis = phases.find((p) => p.phase === "diagnosis");
      const postConsult = phases.find((p) => p.phase === "postConsult");
      const cpap = phases.find((p) => p.phase === "cpapFollowup");

      const diagAmt = diagnosis ? Number(diagnosis.amount || 0) : 0;
      const postAmt = postConsult ? Number(postConsult.amount || 0) : 0;
      const cpapAmt = cpap ? Number(cpap.amount || 0) : 0;

      return {
        No: index + 1,
        Order: e.order || "",
        HN: e.hn || "",
        Firstname: e.firstname || "",
        Lastname: e.lastname || "",
        Node: e.node || "",
        Customer: e.customer || "",
        HealthInsurance: e.healthInsurance || "-",
        "Diagnosis Amount (THB)": diagAmt || 0,
        "Post-Consult Amount (THB)": postAmt || 0,
        "CPAP Follow-up Amount (THB)": cpapAmt || 0,
        "Total (THB)": diagAmt + postAmt + cpapAmt || 0,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = [
      { wch: 4 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 24 },
      { wch: 24 },
      { wch: 16 },
      { wch: 22 }, // Diagnosis
      { wch: 26 }, // Post
      { wch: 28 }, // CPAP
      { wch: 14 }, // Total
    ];

    const m = batch.meta || {};

    const toDate = (ts) => {
      if (!ts) return "";
      if (ts.toDate) return ts.toDate().toLocaleString();
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
      return new Date(ts).toLocaleString();
    };

    const toDateOnly = (ts) => {
      if (!ts) return "";
      if (ts.toDate)
        return ts.toDate().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      if (ts.seconds)
        return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      return new Date(ts).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    // meta lines (เหมือน technician แต่ไม่มี transport/guarantee ถ้าไม่ใช้ก็ใส่ไว้เฉยๆได้)
    const metaLines = [
      ["Amount", money(m.amount || 0)],
      ["WHT 3%", "-" + money(m.wht || 0)],
      ["Deduction", "-" + money(m.deduction || 0)],
      ["Adjustment Pay", "+" + money(m.adjustmentPay || 0)],
      [],
      ["Total Pay", money(m.totalPay || 0)],
      ["Pay Date", toDateOnly(m.payDate)],
      ["Note", (m.payNote || "").toString()], // keep \n
    ];

    const summaryAoA = [
      ["Doctor", doctorName || "-"],
      ["Month", batch.month || ""],
      [
        "Period Start",
        batch.period && batch.period.start ? batch.period.start : "",
      ],
      ["Period End", batch.period && batch.period.end ? batch.period.end : ""],
      ["Confirmed", batch.isConfirmed ? "Yes" : "No"],
      ["Created By", batch.createdBy || ""],
      ["Confirmed By", batch.confirmedBy || ""],
      ["Created At", toDate(batch.createdAt)],
      ["Confirmed At", toDate(batch.confirmedAt)],
      [],
      ...metaLines,
    ];

    const wsMeta = XLSX.utils.aoa_to_sheet(summaryAoA);

    // append meta to bottom of details
    const detailsAppendAoA = [
      [],
      ["--- Payment Summary ---", ""],
      ...metaLines,
    ];
    XLSX.utils.sheet_add_aoa(ws, detailsAppendAoA, { origin: -1 });

    // Summary first tab
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsMeta, "Summary");
    XLSX.utils.book_append_sheet(wb, ws, "Batch Details");

    const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    return new Uint8Array(arrayBuffer);
  };

  const uint8ToBase64 = (u8) => {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < u8.length; i += chunkSize) {
      binary += String.fromCharCode(...u8.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const handleSendEmail = async (batch) => {
    if (!window.confirm("Send email for this confirmed batch?")) return;

    try {
      if (!batch.entries || !batch.entries.length) {
        notifyWarning("No entries to export.");
        return;
      }

      // ===== resolve doctor name =====
      // คุณเปลี่ยน doctorList -> รายชื่อ list ของหมอจริงในหน้าคุณ
      const doctorName =
        selectedDoctor === "Summary"
          ? (doctorList.find((d) => d.doctorId === batch.doctorKey)
              ? doctorList.find((d) => d.doctorId === batch.doctorKey).name
              : batch.doctorId) || "Doctor"
          : selectedDoctor || batch.doctorId || batch.doctorKey || "Doctor";
      console.log("Resolved doctor name for email:", doctorName);
      const safeDoctor = doctorName.replace(/[\\\/:*?\[\]]/g, "_");
      const fileName = `PaymentBatch_${safeDoctor}_${batch.month || ""}.xlsx`;

      // 1) build excel bytes
      const bytes = buildPhysicianPaymentBatchWorkbookBytes(batch, doctorName);

      // 2) base64
      const base64 = uint8ToBase64(bytes);

      // 3) recipient email (ปรับ field ตามของจริง)
      const to =
        (doctorList.find((d) => d.doctorId === batch.doctorKey)
          ? doctorList.find((d) => d.doctorId === batch.doctorKey).email
          : "") ||
        batch.emailTo ||
        batch.doctorEmail ||
        batch.financeEmail ||
        "noppasit3@braindynamics.co.th";

      // const to = "pisanu@braindynamics.co.th";

      if (!to) {
        notifyWarning("No recipient email found for this batch.");
        return;
      }

      const m = batch.meta || {};
      const payNoteHtml = (m.payNote || "-").replace(/\n/g, "<br/>");

      const toDateOnly = (ts) => {
        if (!ts) return "-";
        if (ts.toDate)
          return ts.toDate().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        if (ts.seconds)
          return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        return new Date(ts).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      };

      const html = `
      <h3>Payment Batch</h3>
      <p><strong>Doctor</strong>: ${doctorName}</p>
      <p><strong>Month</strong>: ${batch.month || "-"}</p>

      <div style="border:1px solid #999; padding:12px; background:#eeeeee; border-radius:6px;">
        <h4 style="margin:0 0 8px 0;">Payment Summary</h4>
        <p style="margin:0;">
          <strong>Amount</strong>: ${money(m.amount ? m.amount : 0)} THB<br/>
          <strong>WHT 3%</strong>: -${money(m.wht ? m.wht : 0)} THB<br/>
          <strong>Deduction</strong>: -${money(m.deduction ? m.deduction : 0)} THB<br/>
          <strong>Adjustment Pay</strong>: +${money(m.adjustmentPay ? m.adjustmentPay : 0)} THB<br/><br/>
          <strong>Total Pay</strong>: <strong>${money(m.totalPay ? m.totalPay : 0)} THB</strong><br/>
          <strong>Pay Date</strong>: <strong>${toDateOnly(m.payDate)}</strong><br/>
          <strong>Note</strong>: ${payNoteHtml}
        </p>
      </div>

      <p style="margin-top:12px;">Attached: ${fileName}</p>
      <p><strong>Submitted</strong>: ${currentUser.displayName}</p>
    `;

      console.log("Email HTML content:", html);

      // 4) send mail with base64 attachment
      await setDoc(doc(collection(db, "mail")), {
        to,
        message: {
          subject: `Payment Batch ${batch.month || ""} - ${doctorName}`,
          html,
          attachments: [
            {
              filename: fileName,
              content: base64,
              encoding: "base64",
              contentType:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          ],
        },
      });

      // console.log("Email document created in Firestore for batch:", batch.id);

      // console.log(
      //   "Batch document updated with email status for batch:",
      //   batch.refPath,
      // );
      // console.log(batch);

      // 5) update batch status
      const batchRef = doc(db, batch.refPath);
      await updateDoc(batchRef, {
        isSendEmail: true,
        sendEmailAt: serverTimestamp(),
        sendEmailBy: currentUser.displayName,
        sendEmailAttachmentName: fileName,
        sendEmailAttachmentMode: "base64",
      });

      notifyTopRight("Email sent with attachment.");

      // ✅ don't filter; just update UI
      setReadyToEmail((prev) =>
        prev.map((b) =>
          b.id === batch.id
            ? {
                ...b,
                isSendEmail: true,
                sendEmailAt: new Date(),
                sendEmailBy: currentUser.displayName,
              }
            : b,
        ),
      );
    } catch (e) {
      console.error(e);
      notifyError("Send email failed", e.message || e);
    }
  };
  // const PHASE_TO_BOOKING_FLAG = {
  //   diagnosis: "isPaidDiagnosis",
  //   post: "isPaidPostConsult",
  //   cpap: "isPaidCPAPFollowup",
  // };

  // const handleExport = async () => {

  //   const snapshot = await getDocs(collection(db, "doctors"));

  //   const doctorIds = snapshot.docs.map((doc) => doc.data().doctorId);

  //   try {
  //     console.log("Starting diagnosis mismatch audit...");
  //     const results = [];
  //     const bookingRefsToUpdate = [];

  //     const q = query(
  //       collectionGroup(db, "batches"),
  //       where("doctorId", "in", doctorIds)
  //     );

  //     const batchesSnap = await getDocs(q);
  //     console.log(`Found ${batchesSnap.size} batches`);

  //     for (const batchDoc of batchesSnap.docs) {
  //       const { entries = [] } = batchDoc.data();

  //       const paymentBatchId = batchDoc.ref.parent.parent.id;
  //       const batchId = batchDoc.id;

  //       for (const entry of entries) {
  //         // 1️⃣ entry must include diagnosis phase
  //         const hasDiagnosisPhase =
  //           Array.isArray(entry.phases) &&
  //           entry.phases.some((p) => p.phase === "diagnosis");

  //         if (!hasDiagnosisPhase) continue;

  //         const bookingRefPath = entry.bookingRefPath;
  //         if (!bookingRefPath) continue;

  //         const bookingRef = doc(db, bookingRefPath);
  //         const bookingSnap = await getDoc(bookingRef);
  //         if (!bookingSnap.exists()) continue;

  //         const booking = bookingSnap.data();

  //         // 2️⃣ mismatch condition
  //         const isPaidDiagnosisBooking =
  //           booking.extendedProps.isPaidDiagnosis === true;

  //         if (isPaidDiagnosisBooking) continue;

  //         // 3️⃣ queue update
  //         bookingRefsToUpdate.push(bookingRef);

  //         results.push({
  //           order: booking.extendedProps.order || "",
  //           patientName: `${booking.extendedProps.firstname || ""} ${
  //             booking.extendedProps.lastname || ""
  //           }`,
  //           hn: booking.extendedProps.hn || "",
  //           paymentBatchId,
  //           batchId,
  //           bookingRefPath,
  //         });
  //       }
  //     }

  //     if (results.length === 0) {
  //       alert("No mismatched diagnosis payments found.");
  //       return;
  //     }

  //     // 🔧 UPDATE BOOKINGS (safe partial update)
  //     console.log(`Updating ${bookingRefsToUpdate.length} bookings...`);

  //     for (const bookingRef of bookingRefsToUpdate) {
  //       await updateDoc(bookingRef, {
  //         "extendedProps.isPaidDiagnosis": true,
  //       });
  //     }

  //     console.log("Booking updates completed.");

  //     // 📊 EXPORT
  //     results.sort((a, b) => a.order.localeCompare(b.order));
  //     exportToExcel(results);
  //   } catch (err) {
  //     console.error("Export error:", err);
  //     alert("Export failed");
  //   }
  // };

  const PHASE_TO_BOOKING_FLAG = {
    diagnosis: "isPaidDiagnosis",
    post: "isPaidPostConsult",
    cpap: "isPaidCPAPFollowup",
  };

  const handleExport = async () => {
    try {
      console.log("Starting payment mismatch audit...");

      // 1️⃣ load doctorIds
      const snapshot = await getDocs(collection(db, "doctors"));
      const doctorIds = snapshot.docs
        .map((d) => d.data().doctorId)
        .filter(Boolean);

      if (doctorIds.length === 0) {
        alert("No doctor IDs found.");
        return;
      }

      const results = [];
      const updates = [];

      // 🔑 Firestore "in" limit = 10 → chunk
      const chunks = [];
      for (let i = 0; i < doctorIds.length; i += 10) {
        chunks.push(doctorIds.slice(i, i + 10));
      }

      for (const chunk of chunks) {
        const q = query(
          collectionGroup(db, "batches"),
          where("doctorId", "in", chunk),
        );

        const batchesSnap = await getDocs(q);
        console.log(`Found ${batchesSnap.size} batches`);

        for (const batchDoc of batchesSnap.docs) {
          const { entries = [] } = batchDoc.data();
          const paymentBatchId = batchDoc.ref.parent.parent.id;
          const batchId = batchDoc.id;

          for (const entry of entries) {
            if (!Array.isArray(entry.phases)) continue;
            if (!entry.bookingRefPath) continue;

            const bookingRef = doc(db, entry.bookingRefPath);
            const bookingSnap = await getDoc(bookingRef);
            if (!bookingSnap.exists()) continue;

            const booking = bookingSnap.data();
            const ep = booking.extendedProps || {};

            // 🔁 check ALL phases in this entry
            for (const p of entry.phases) {
              const phase = p.phase;
              const paidFlag = PHASE_TO_BOOKING_FLAG[phase];

              if (!paidFlag) {
                console.warn("Unknown phase:", phase);
                continue;
              }

              // already paid → OK
              if (ep[paidFlag] === true) continue;

              // ❌ mismatch found
              results.push({
                order: ep.order || "",
                phase,
                patientName: `${ep.firstname || ""} ${ep.lastname || ""}`,
                hn: ep.hn || "",
                paymentBatchId,
                batchId,
                bookingRefPath: entry.bookingRefPath,
              });

              updates.push({
                ref: bookingRef,
                flag: paidFlag,
              });
            }
          }
        }
      }

      if (results.length === 0) {
        alert("No payment mismatches found.");
        return;
      }

      // 2️⃣ batch update (safe, partial, non-destructive)
      console.log(`Updating ${updates.length} booking phase flags...`);

      let wb = writeBatch(db);
      let ops = 0;

      for (const u of updates) {
        wb.update(u.ref, {
          [`extendedProps.${u.flag}`]: true,
        });
        ops++;

        if (ops === 500) {
          await wb.commit();
          wb = writeBatch(db);
          ops = 0;
        }
      }

      if (ops > 0) await wb.commit();

      console.log("Booking updates completed.");

      // 3️⃣ export
      results.sort(
        (a, b) =>
          a.order.localeCompare(b.order) || a.phase.localeCompare(b.phase),
      );

      exportToExcel(results);
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed");
    }
  };

  if (loading) {
    return <DashboardLoading />;
  }

  if (!canManagePayments && !canViewOverall) {
    return (
      <React.Fragment>
        <Toast />
        <PageTitle activeMenu="Doctor Payments" motherMenu="Dashboard" />
        <div className="card">
          <div className="card-body">
            <h4 className="mb-2">Payment access is not configured</h4>
            <p className="mb-0 text-muted">
              Ask a super admin to map your account email to the exact doctor
              name used in booking data before you can create or edit payments.
            </p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Toast />
      {/* Title */}
      <div className="mb-3 text-center">
        <h2>
          ภาพรวมประจำเดือน{" "}
          <strong>{getThaiMonth(new Date().getMonth())}</strong>
        </h2>
      </div>
      <div className="row mb-3">
        {paymentAccess ? (
          <Dropdown
            className="align-items-end d-inline-flex ml-2"
            onSelect={SelectDoctor}
          >
          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
            <i className="flaticon-381-controls-3"></i> by {selectedDoctor}
          </Dropdown.Toggle>
          {/* <Dropdown.Menu className="dropdown-menu-right">
            <Dropdown.Item key={"Summary"} href="#" eventKey={"Summary"}>
              {"Summary"}
            </Dropdown.Item>
            {doctorList.map((doctor) => (
              <Dropdown.Item key={doctor.id} href="#" eventKey={doctor.name}>
                {doctor.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu> */}
          <Dropdown.Menu
            className="dropdown-menu-right"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <Dropdown.Item key={"Summary"} href="#" eventKey={"Summary"}>
              Summary
            </Dropdown.Item>
            {doctorList.map((doctor) => (
              <Dropdown.Item key={doctor.id} href="#" eventKey={doctor.name}>
                {doctor.name} (
                {String(doctor.fullnameTh || "")
                  .split(/\s+/)
                  .slice(0, 2)
                  .join(" ")})
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
          </Dropdown>
        ) : (
          <div className="align-items-center d-inline-flex ml-2">
            <span className="badge badge-outline-primary p-2">
              <i className="flaticon-381-controls-3"></i>{" "}
              {canViewOverall && !canManagePayments
                ? "Overall view — payment management disabled"
                : `by ${scopedDoctorName}`}
            </span>
          </div>
        )}

        {canManagePayments && (
          <React.Fragment>
            <div className="ml-3 d-inline-flex">
              <label className="mr-2 mb-0 align-self-center">Start:</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="ml-3 d-inline-flex">
              <label className="mr-2 mb-0 align-self-center">End:</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="ml-3 d-inline-flex">
              <Button className="btn-danger" onClick={Export}>
                Query
              </Button>
              {/* <Button onClick={handleExport}>MISMATCH PAID</Button> */}
            </div>
          </React.Fragment>
        )}
      </div>

      {/* <Button
        className="ml-2"
        variant="outline-danger"
        onClick={() => fixAllPostConsultPaidFalse()}
      >
        Fix Post-Consult Paid Flags
      </Button> */}

      {/* Tabs */}
      <Tabs
        id="doctor-dashboard-tabs"
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
          setBatchEdit(null);
          setSelectedBatch(null);
        }}
        className="mb-3"
        mountOnEnter
        unmountOnExit
      >
        {/* ---- Overview tab (cards) ---- */}
        {canViewOverall && <Tab eventKey="overview" title="Overview">
          <div className="row">
            {doctorsSortedByRecord.map((doctor) => {
              const today = new Date();
              const year = today.getFullYear();
              const month = today.getMonth();

              const startOfMonth = new Date(year, month, 1, 0, 0, 0);
              const formattedStartOfMonth = formatDate(startOfMonth);
              const endOfYesterday = new Date(
                year,
                month,
                today.getDate() - 1,
                23,
                59,
                59,
              );
              const formattedEndOfYesterday = formatDate(endOfYesterday);

              const recordList = activityList.filter(
                (activity) =>
                  activity.extendedProps.doctor === doctor.name &&
                  !excludeStatus.includes(activity.extendedProps.status),
              );
              const scoredHnSet = new Set(
                scoredList
                  .map((activity) =>
                    normalizeHn(
                      activity.hn ||
                        (activity.extendedProps && activity.extendedProps.hn),
                    ),
                  )
                  .filter(Boolean),
              );

              const recordedList = recordList.filter((activity) => {
                const scheduled = activity.extendedProps.latestSleepStudyDate;
                return (
                  scheduled >= formattedStartOfMonth &&
                  scheduled <= formattedEndOfYesterday
                );
              });

              const matchedScoredBookings = allActivityList.filter((booking) =>
                scoredHnSet.has(
                  normalizeHn(
                    booking.extendedProps && booking.extendedProps.hn,
                  ),
                ) &&
                booking.extendedProps.doctor === doctor.name &&
                !excludeStatus.includes(booking.extendedProps.status),
              );
              const scheduledNodeSummary = getScheduledNodeSummary(
                matchedScoredBookings,
                nodeCodeByName,
              );

              return (
                <div className="col-12 col-md-6 col-lg-4" key={doctor.id}>
                  <Card
                    className="mb-3 shadow-sm"
                    style={{ borderRadius: "10px" }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                          <img
                            src={avatar}
                            alt=""
                            className="rounded-circle me-4"
                            width="50"
                          />
                          <div
                            style={{ maxWidth: "250px", marginLeft: "1rem" }}
                          >
                            <h5 className="mb-0 fw-semibold text-break">
                              {doctor.name} (
                              {doctor.fullnameTh
                                .split(/\s+/)
                                .slice(0, 2)
                                .join(" ")}
                              )
                            </h5>
                            <p className="mb-0 text-muted text-wrap text-break">
                              {doctor.email}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          <h6
                            className="text-primary text-center"
                            style={{ width: "100px" }}
                          >
                            Scheduled
                          </h6>
                          <h1 className="mb-0 text-primary text-center">
                            {recordList.length}
                          </h1>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="row">
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row">
                                <h6 style={{ width: "100px" }}>Scored:</h6>
                                <h5 className="mb-0 ms-3">
                                  {matchedScoredBookings.length}
                                </h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-top">
                        <div className="d-flex align-items-center">
                          <h6
                            className="mb-0 text-muted"
                            style={{ width: "100px", flexShrink: 0 }}
                          >
                            Node:
                          </h6>
                          <div
                            className="d-flex flex-row flex-wrap align-items-center"
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            {scheduledNodeSummary.length > 0 ? (
                              scheduledNodeSummary.map(
                                ({ node, label, count }) => (
                                  <span
                                    key={node}
                                    className="badge badge-light border text-dark mr-2 d-inline-flex align-items-center flex-shrink-0"
                                    title={`${node}: ${count} scheduled`}
                                    style={{
                                      width: "calc(33.333% - 0.5rem)",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <span
                                      className="d-inline-block text-truncate align-bottom"
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {label}
                                    </span>
                                    <span className="badge badge-primary ml-1">
                                      {count}
                                    </span>
                                  </span>
                                ),
                              )
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>
        </Tab>}

        {/* ---- Payments tab ---- */}
        {canManagePayments && <Tab eventKey="payments" title="Payments">
          {/* Filters specific to payments */}

          {/* Payment table (kept your existing condition) */}
          {bookingTmpList.length > 0 &&
            canManagePayments && (
              <div className="row">
                <div>
                  <h3>
                    {doctorList.find((doc) => doc.name === selectedDoctor).name}
                  </h3>
                  <h3>
                    {
                      doctorList.find((doc) => doc.name === selectedDoctor)
                        .email
                    }
                  </h3>
                  <div className="d-flex align-items-end mb-3">
                    <div className="mr-2">
                      <label className="mb-1">Add by Order</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. CHG-SS-123"
                        onChange={(e) =>
                          (manualOrderRef.current = e.target.value)
                        }
                        // onKeyDown={(e) => {
                        //   if (e.key === "Enter")
                        //     addBookingByOrder(manualOrderRef.current);
                        // }}
                      />
                    </div>
                    <Button className="ml-2 mb-1" onClick={addBookingByOrder}>
                      Add
                    </Button>
                  </div>
                </div>
                <Table className="table-responsive-12 mb-2 table-striped">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="align-middle">
                        Doctor Scored Date
                      </th>
                      <th rowSpan="2" className="align-middle">
                        Order
                      </th>
                      <th rowSpan="2" className="align-middle">
                        Patient
                      </th>
                      {/* <th rowSpan="2" className="text-center align-middle">
                        Doctor
                      </th> */}
                      <th rowSpan="2" className="align-middle">
                        Health Insurance
                      </th>
                      {/* <th rowSpan="2" className="align-middle">
                        Current Status
                      </th> */}

                      {/* Group 1: Paid Status */}
                      <th colSpan="3" className="text-center">
                        Paid Status
                      </th>

                      {/* Group 2: Payment (This Round) */}
                      <th colSpan="3" className="text-center">
                        Phases (This Batch){" "}
                      </th>

                      <th rowSpan="2" className="text-center">
                        Total (THB)
                      </th>
                      <th rowSpan="2" className="align-middle text-end"></th>
                    </tr>
                    <tr>
                      {/* Paid sub-cols */}
                      <th className="text-center whitespace-nowrap">
                        Diagnosis
                      </th>
                      <th className="text-center whitespace-nowrap">
                        Post-consult
                      </th>
                      <th className="text-center whitespace-nowrap">
                        Follow-up
                      </th>

                      {/* Payment sub-cols with Select All */}
                      <th className="text-center whitespace-nowrap">
                        <div className="d-flex flex-column align-items-center justify-content-center whitespace-nowrap">
                          <span className="fw-bold text-uppercase mb-4">
                            Diagnosis
                          </span>
                          <input
                            ref={diagAllRef}
                            type="checkbox"
                            className="form-check-input header-check"
                            checked={!!headerState.diagnosis.checked}
                            onChange={toggleAllPhase("diagnosis")}
                            disabled={!!headerState.diagnosis.disabled}
                          />
                        </div>
                      </th>

                      <th className="text-center whitespace-nowrap">
                        <div className="d-flex flex-column align-items-center justify-content-center whitespace-nowrap">
                          <span className="fw-bold text-uppercase mb-4">
                            Post-Consult
                          </span>{" "}
                          <input
                            ref={postAllRef}
                            type="checkbox"
                            className="form-check-input header-check"
                            checked={!!headerState.post.checked}
                            onChange={toggleAllPhase("post")}
                            disabled={!!headerState.post.disabled}
                          />
                        </div>
                      </th>

                      <th className="text-center whitespace-nowrap">
                        <div className="d-flex flex-column align-items-center justify-content-center whitespace-nowrap">
                          <span className="fw-bold text-uppercase mb-4">
                            Follow-Up
                          </span>
                          <input
                            ref={cpapAllRef}
                            type="checkbox"
                            className="form-check-input header-check"
                            checked={!!headerState.cpap.checked}
                            onChange={toggleAllPhase("cpap")}
                            disabled={!!headerState.cpap.disabled}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingTmpList.map((row, index) => {
                      const ep = row.extendedProps || {};
                      const ord = ep.order;
                      const sel = payThisRound[ord] || {
                        diagnosis: false,
                        post: false,
                        cpap: false,
                      };

                      const type = ep.type || "";
                      var typeSymbol = "";

                      const paidDiag = !!ep.isPaidDiagnosis;
                      const paidPost = !!ep.isPaidPostConsult;
                      const paidCpap = !!ep.isPaidCPAPFollowup;

                      const doctor = doctorList.find(
                        (doc) => doc.name === selectedDoctor,
                      );
                      const sum =
                        (sel.diagnosis
                          ? getRate(doctor, row, "diagnosis")
                          : 0) +
                        (sel.post ? getRate(doctor, row, "post") : 0) +
                        (sel.cpap ? getRate(doctor, row, "cpap") : 0);

                      var typeSymbol = "";
                      if (type === "Split Night Sleep Study") {
                        typeSymbol = "[SN]";
                      } else if (type === "Full Night Sleep Study") {
                        typeSymbol = "[FN]";
                      } else if (type === "Home Full Night Sleep Study") {
                        typeSymbol = "[HN]";
                      } else if (
                        type === "CPAP Titration" ||
                        type === "CPAP Titration Study"
                      ) {
                        typeSymbol = "[CT]";
                      } else if (type === "Overnight Pulse Oximetry Study") {
                        typeSymbol = "[OPO]";
                      } else if (type === "Multiple Sleep Latency Test") {
                        typeSymbol = "[MSLT]";
                      }

                      return (
                        <tr key={row.id || ord}>
                          <td>{activityTmpList[index].ScoredAt || ""}</td>
                          <td className="no-wrap">
                            {ord} {typeSymbol}
                          </td>
                          <td>
                            {ep.firstname} {ep.lastname}
                          </td>
                          <td>{ep.healthInsurance}</td>
                          {/* <td>{ep.status}</td> */}
                          {/* Paid Status (icons) */}
                          <td className="text-center align-middle">
                            <PaidIcon on={paidDiag} />
                          </td>
                          <td className="text-center align-middle">
                            <PaidIcon on={paidPost} />
                          </td>
                          <td className="text-center align-middle">
                            <PaidIcon on={paidCpap} />
                          </td>

                          {/* Payment (This Round) */}
                          <td className="text-center align-middle">
                            <div className="form-check d-flex justify-content-center align-items-center m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!sel.diagnosis}
                                onChange={togglePay(
                                  ord,
                                  "diagnosis",
                                  setPayThisRound,
                                )}
                                disabled={paidDiag}
                              />
                            </div>
                          </td>
                          <td className="text-center align-middle">
                            <div className="form-check d-flex justify-content-center align-items-center m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!sel.post}
                                onChange={togglePay(
                                  ord,
                                  "post",
                                  setPayThisRound,
                                )}
                                disabled={paidPost}
                              />
                            </div>
                          </td>
                          <td className="text-center align-middle">
                            <div className="form-check d-flex justify-content-center align-items-center m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!sel.cpap}
                                onChange={togglePay(
                                  ord,
                                  "cpap",
                                  setPayThisRound,
                                )}
                                disabled={paidCpap}
                              />
                            </div>
                          </td>

                          <td className="text-center">{money(sum)}</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeBooking(ord)}
                            >
                              <i className="fa fa-trash"></i>{" "}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {/* Footer: grand total + action */}
                {(() => {
                  const doctor = doctorList.find(
                    (doc) => doc.name === selectedDoctor,
                  );

                  // 1) amount: sum of selected phases
                  const amount = bookingTmpList.reduce((acc, row) => {
                    const ep = row.extendedProps || {};
                    const ord = ep.order;
                    const sel = payThisRound[ord] || {};
                    return (
                      acc +
                      (sel.diagnosis ? getRate(doctor, row, "diagnosis") : 0) +
                      (sel.post ? getRate(doctor, row, "post") : 0) +
                      (sel.cpap ? getRate(doctor, row, "cpap") : 0)
                    );
                  }, 0);

                  // 2) WHT 3%
                  const wht = amount * 0.03;

                  // 3) Deduction (user input)
                  const ded = toNumber(deduction);

                  const adjust = toNumber(adjustmentPay);

                  const note = payNote.trim();

                  // 4) Total pay
                  const totalPay = Math.max(0, amount - wht - ded + adjust);

                  // Build entries from selections (grouped by order with phases)
                  // const buildEntries = () => {
                  //   const byOrder = new Map();
                  //   bookingTmpList.forEach((row) => {
                  //     const ep = row.extendedProps || {};
                  //     const ord = ep.order;
                  //     if (!ord) return;
                  //     const sel = payThisRound[ord] || {};
                  //     const phases = [];
                  //     if (sel.diagnosis)
                  //       phases.push({
                  //         phase: "diagnosis",
                  //         amount: getRate(doctor, row, "diagnosis"),
                  //       });
                  //     if (sel.post)
                  //       phases.push({
                  //         phase: "postConsult",
                  //         amount: getRate(doctor, row, "post"),
                  //       });
                  //     if (sel.cpap)
                  //       phases.push({
                  //         phase: "cpapFollowup",
                  //         amount: getRate(doctor, row, "cpap"),
                  //       });
                  //     if (!phases.length) return;

                  //     const existing = byOrder.get(ord);
                  //     if (existing) {
                  //       existing.phases.push(...phases);
                  //     } else {
                  //       byOrder.set(ord, {
                  //         order: ord,
                  //         bookingRefPath: row.refPath, // ensure you carried refPath when querying
                  //         phases,
                  //       });
                  //     }
                  //   });
                  //   return Array.from(byOrder.values());
                  // };

                  const buildEntries = () => {
                    const byOrder = new Map();

                    bookingTmpList.forEach((row) => {
                      const ep = row.extendedProps || {};
                      const ord = ep.order;
                      if (!ord) return;
                      if (
                        !paymentAccess &&
                        ep.doctor !== scopedDoctorName &&
                        ep.postDoctor !== scopedDoctorName
                      ) {
                        return;
                      }

                      const sel = payThisRound[ord] || {};
                      const phases = [];

                      if (sel.diagnosis)
                        phases.push({
                          phase: "diagnosis",
                          amount: getRate(doctor, row, "diagnosis"),
                        });
                      if (sel.post)
                        phases.push({
                          phase: "postConsult",
                          amount: getRate(doctor, row, "post"),
                        });
                      if (sel.cpap)
                        phases.push({
                          phase: "cpapFollowup",
                          amount: getRate(doctor, row, "cpap"),
                        });

                      if (!phases.length) return;

                      // Snapshot of current paid status from booking
                      const paid = {
                        diagnosis: {
                          isPaid: !!ep.isPaidDiagnosis,
                          paidDate: ep.paidDiagnosisDate || null,
                          batchId: ep.paidDiagnosisBatchId || null,
                        },
                        postConsult: {
                          isPaid: !!ep.isPaidPostConsult,
                          paidDate: ep.paidPostConsultDate || null,
                          batchId: ep.paidPostConsultBatchId || null,
                        },
                        cpapFollowup: {
                          isPaid: !!ep.isPaidCPAPFollowup,
                          paidDate: ep.paidCPAPFollowupDate || null,
                          batchId: ep.paidCPAPFollowupBatchId || null,
                        },
                      };

                      const baseEntry = {
                        order: ord,
                        bookingRefPath: row.refPath, // keep for later confirm step
                        hn: ep.hn || "",
                        node: ep.node || "",
                        customer: ep.customer || "",
                        firstname: ep.firstname || "",
                        lastname: ep.lastname || "",
                        healthInsurance: ep.healthInsurance || "",
                        paid, // 👈 add snapshot here
                        phases, // selected phases + amounts this round
                      };

                      const existing = byOrder.get(ord);
                      if (existing) {
                        existing.phases.push(...phases);
                        // merge is unnecessary for "paid" snapshot; first one is fine
                      } else {
                        byOrder.set(ord, baseEntry);
                      }
                    });

                    return Array.from(byOrder.values());
                  };

                  const onCreatePayment = async () => {
                    if (
                      !canManagePayments ||
                      (!paymentAccess && selectedDoctor !== scopedDoctorName)
                    ) {
                      notifyWarning("You can only create payments for your own cases.");
                      return;
                    }

                    const doctor = doctorList.find(
                      (d) => d.name === selectedDoctor,
                    );
                    if (!doctor) {
                      notifyWarning("Doctor not found.");
                      return;
                    }

                    const entries = buildEntries();
                    if (!entries.length) {
                      notifyWarning("Please select at least one item to pay.");
                      return;
                    }
                    if (!payDate) {
                      notifyWarning("Please choose a pay date.");
                      return;
                    }

                    // doctorKey used as parent doc id under paymentBatches/{doctorKey}/batches/{autoId}
                    const doctorKey =
                      (doctor.doctorId || "").trim() ||
                      (selectedDoctor || "").trim();

                    // Compute totals (assumes amount, wht, ded, totalPay already computed in your block)
                    const payload = {
                      doctorId: doctor.doctorId || doctor.id || selectedDoctor,
                      // month: payDate.slice(0, 7), // "YYYY-MM"
                      month: startDate.slice(0, 7), // "YYYY-MM"
                      period: { start: startDate || "", end: endDate || "" },
                      entries,
                      isConfirmed: false, // draft by default
                      createdBy: currentUser.displayName,
                      createdAt: new Date(),
                      confirmedBy: null,
                      confirmedAt: null,
                      meta: {
                        amount,
                        wht,
                        deduction: ded,
                        totalPay,
                        adjustmentPay: adjust,
                        payNote: note,
                        payDate, // "YYYY-MM-DD"
                      },
                    };

                    // Convert to Firestore-friendly doc
                    const batchDoc = {
                      doctorId: payload.doctorId,
                      month: payload.month,
                      period: payload.period,
                      entries: payload.entries, // [{ order, bookingRefPath, phases:[{phase, amount}]}]
                      isConfirmed: false,
                      createdBy: payload.createdBy,
                      createdAt: serverTimestamp(),
                      confirmedBy: null,
                      confirmedAt: null,
                      meta: {
                        amount: Number(payload.meta.amount || 0),
                        wht: Number(payload.meta.wht || 0),
                        deduction: Number(payload.meta.deduction || 0),
                        totalPay: Number(payload.meta.totalPay || 0),
                        adjustmentPay: Number(payload.meta.adjustmentPay || 0),
                        payNote: payload.meta.payNote || "",
                        payDate: Timestamp.fromDate(
                          new Date(`${payload.meta.payDate}T00:00:00`),
                        ),
                      },
                    };
                    console.log("Batch to create:", batchDoc);

                    try {
                      const ref = await addDoc(
                        collection(db, "paymentBatches", doctorKey, "batches"),
                        batchDoc,
                      );
                      loadPendingBatches();
                      loadReadyToEmailForDoctor();
                      notifyTopRight(`Draft created (Batch ID: ${ref.id}).`);
                    } catch (err) {
                      console.error(err);
                      notifyError("Create payment failed", err.message || err);
                    }
                  };

                  const btnDisabled = amount <= 0 || !payDate;

                  const countsByPhase = bookingTmpList.reduce(
                    (acc, row) => {
                      const ep = row.extendedProps || {};
                      const ord = ep.order;
                      const sel = payThisRound[ord] || {};

                      if (sel.diagnosis) acc.diagnosis += 1;
                      if (sel.post) acc.post += 1;
                      if (sel.cpap) acc.cpap += 1;

                      return acc;
                    },
                    { diagnosis: 0, post: 0, cpap: 0 },
                  );

                  return (
                    <div className="row p-3 w-100 justify-content-end">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="border rounded shadow-sm bg-white p-3">
                          {/* Header */}
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                              <div className="fw-bold">Payment Summary</div>
                            </div>
                          </div>

                          {/* Counts */}
                          <div className="border rounded p-2 mb-3">
                            <div className="d-flex gap-2 flex-wrap">
                              <div className="px-2 py-1 rounded border d-flex align-items-center gap-2 mr-3">
                                <span
                                  className="text-muted"
                                  style={{ fontSize: 12 }}
                                >
                                  Diagnosis
                                </span>
                                <span className="fw-bold ml-3">
                                  {countsByPhase.diagnosis}
                                </span>
                              </div>
                              <div className="px-2 py-1 rounded border d-flex align-items-center gap-2 mr-3">
                                <span
                                  className="text-muted"
                                  style={{ fontSize: 12 }}
                                >
                                  Post consult
                                </span>
                                <span className="fw-bold ml-3">
                                  {countsByPhase.post}
                                </span>
                              </div>
                              <div className="px-2 py-1 rounded border d-flex align-items-center gap-2">
                                <span
                                  className="text-muted"
                                  style={{ fontSize: 12 }}
                                >
                                  CPAP follow-up
                                </span>
                                <span className="fw-bold ml-3">
                                  {countsByPhase.cpap}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Money summary */}
                          <div className="border rounded p-2 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Amount</span>
                              <strong>{money(amount)} THB</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">WHT 3%</span>
                              <strong>- {money(wht)} THB</strong>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="mb-0 text-muted">
                                Deduction
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold">-</span>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end"
                                  style={{ width: 160 }}
                                  value={deduction}
                                  onChange={(e) => setDeduction(e.target.value)}
                                  min="0"
                                />
                              </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <label className="mb-0 text-muted">
                                Adjustment Pay
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold">+</span>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end"
                                  style={{ width: 160 }}
                                  value={adjustmentPay}
                                  onChange={(e) =>
                                    setAdjustmentPay(e.target.value)
                                  }
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Note */}
                          <div className="mb-3">
                            <label className="mb-1">Payment Note</label>

                            <textarea
                              className="form-control form-control-sm"
                              value={payNote}
                              onChange={(e) => setPayNote(e.target.value)}
                              rows={3}
                            />
                          </div>

                          {/* Total + pay date */}
                          <div className="border-top pt-3">
                            <div className="d-flex justify-content-between align-items-end mb-2">
                              <span className="text-muted">Total Pay</span>
                              <div className="text-end">
                                <div
                                  className="fw-bold"
                                  style={{ fontSize: 22 }}
                                >
                                  {money(totalPay)} THB
                                </div>
                              </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <label className="mb-0 text-muted">
                                Pay Date
                              </label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                style={{ width: 180 }}
                                value={payDate}
                                onChange={(e) => setPayDate(e.target.value)}
                              />
                            </div>

                            <div className="d-flex justify-content-end">
                              <Button
                                onClick={onCreatePayment}
                                disabled={btnDisabled || !canManagePayments}
                                title={
                                  btnDisabled
                                    ? "Select items and choose a pay date"
                                    : "Create Payment"
                                }
                              >
                                Create Payment
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
        </Tab>}

        {/* ---- Pending tab ---- */}
        {canManagePayments && <Tab eventKey="pending" title="Pending Payments">
          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-1">
                  Pending Payments{" "}
                  <span className="badge badge-warning">{pendingBatches.length}</span>
                </h4>
                <small className="text-muted">
                  Drafts can be reviewed and edited before confirmation.
                </small>
              </div>
              <Button
                variant="light"
                size="sm"
                onClick={loadPendingBatches}
                disabled={loadingPending}
              >
                Refresh
              </Button>
            </div>

            {loadingPending && <div>Loading…</div>}

            {!loadingPending && pendingBatches.length === 0 && (
              <div className="text-muted">
                No pending batches for this doctor.
              </div>
            )}

            {!loadingPending &&
              pendingBatches.length > 0 &&
              canManagePayments && (
                <Table
                  responsive
                  hover
                  className="table-striped table-sm mb-0"
                  style={{ minWidth: 900 }}
                >
                  <thead className="thead-light">
                    <tr>
                      <th>Draft</th>
                      <th>Doctor</th>
                      <th>Payment period</th>
                      <th>Payment summary</th>
                      <th className="text-right">Net pay</th>
                      <th className="text-center">Cases</th>
                      <th>Created by</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...pendingBatches]
                      .sort(
                        (a, b) =>
                          paymentTimestamp(b.createdAt) -
                          paymentTimestamp(a.createdAt),
                      )
                      .map((b) => {
                        const meta = b.meta || {};
                        const doctor =
                          (doctorList.find(
                            (item) => item.doctorId === b.doctorKey,
                          ) || {}).name ||
                          b.doctorId ||
                          b.doctorKey ||
                          "-";

                        return (
                        <tr key={`${b.doctorKey}-${b.id}`} className="align-middle">
                          <td>
                            <span className="badge badge-warning">Draft</span>
                            <small className="d-block text-muted mt-1">
                              {formatPaymentDate(b.createdAt, true)}
                            </small>
                            <small className="d-block text-muted">
                              #{String(b.id || "").slice(-8)}
                            </small>
                          </td>
                          <td>
                            <div className="font-weight-bold">{doctor}</div>
                            <small className="text-muted">{b.doctorKey || "-"}</small>
                          </td>
                          <td>
                            <span className="badge badge-light">{b.month || "-"}</span>
                            <small className="d-block text-muted mt-1">
                              Pay: {formatPaymentDate(meta.payDate)}
                            </small>
                          </td>
                          <td>
                            <div>
                              Gross: <strong>{money(meta.amount || 0)} THB</strong>
                            </div>
                            <small className="text-muted">
                              WHT -{money(meta.wht || 0)} · Deduction -
                              {money(meta.deduction || 0)}
                            </small>
                          </td>
                          <td className="text-right">
                            <strong className="text-primary">
                              {money(meta.totalPay || 0)} THB
                            </strong>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-secondary">
                              {(b.entries || []).length}
                            </span>
                          </td>
                          <td>{b.createdBy || "-"}</td>
                          <td className="text-right">
                            <div className="d-flex flex-wrap justify-content-end">
                              <Button
                                size="sm"
                                className="mr-2 mb-1"
                                variant="secondary"
                                onClick={() => openBatchForReview(b)}
                              >
                                Review
                              </Button>
                              <Button
                                size="sm"
                                className="mr-2 mb-1"
                                variant="outline-primary"
                                onClick={() => startBatchEdit(b)}
                              >
                                Edit
                              </Button>
                              {paymentAccess && (
                                <Button
                                  size="sm"
                                  className="mb-1"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteBatch(b.id)}
                                  aria-label={`Delete draft ${b.id}`}
                                  title="Delete draft"
                                >
                                  <i className="fa fa-trash"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </Table>
              )}

            {/* Review Drawer/Modal (inline simple block here) */}
            {selectedBatch && (
              <div className="card mt-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      {editingSelectedBatch ? "Edit Draft Batch" : "Review Batch"}: {" "}
                      {selectedBatch.id}
                    </h5>
                    <Button
                      variant="light"
                      className="btn btn-sm mr-2"
                      size="sm"
                      onClick={() => {
                        setBatchEdit(null);
                        setSelectedBatch(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>

                  {editingSelectedBatch && (
                    <div className="text-muted small mb-2">
                      Update the included phase amounts and payment details, then
                      save the draft before confirming it.
                    </div>
                  )}

                  {/* === Review Table: Paid Status + Phases grouped === */}
                  <Table className="table-sm table-striped">
                    <thead>
                      <tr>
                        <th rowSpan="2" className="align-middle">
                          Order
                        </th>
                        <th rowSpan="2" className="align-middle">
                          Patient
                        </th>
                        <th rowSpan="2" className="align-middle">
                          Health Insurance
                        </th>

                        {/* Group 1: Paid Status snapshot from booking at draft time */}
                        <th colSpan="3" className="text-center">
                          Paid Status
                        </th>

                        {/* Group 2: Phases included in THIS batch */}
                        <th colSpan="3" className="text-center">
                          Phases (This Batch)
                        </th>

                        <th rowSpan="2" className="align-middle text-end">
                          Total (THB)
                        </th>
                      </tr>
                      <tr>
                        {/* Paid Status subcols */}
                        <th className="text-center">Diagnosis</th>
                        <th className="text-center">Post-consult</th>
                        <th className="text-center">Follow-up</th>
                        {/* Phases subcols */}
                        <th className="text-center">Diagnosis</th>
                        <th className="text-center">Post-consult</th>
                        <th className="text-center">Follow-up</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(editingSelectedBatch
                        ? batchEdit.entries
                        : selectedBatch.entries || []
                      ).map((e, i) => {
                        const rowTotal = (e.phases || []).reduce(
                          (s, p) => s + Math.max(0, toNumber(p.amount)),
                          0,
                        );

                        // helpers for “this batch” phases
                        const hasPhase = (key) =>
                          (e.phases || []).some((p) => p.phase === key);
                        const phaseAmount = (key) =>
                          (e.phases || [])
                            .filter((p) => p.phase === key)
                            .reduce(
                              (s, p) => s + Math.max(0, toNumber(p.amount)),
                              0,
                            );
                        const phaseIndex = (key) =>
                          (e.phases || []).findIndex((p) => p.phase === key);

                        // paid snapshot
                        const paid = e.paid || {};
                        const paidDiag = !!paid.diagnosis.isPaid;
                        const paidPost = !!paid.postConsult.isPaid;
                        const paidCpap = !!paid.cpapFollowup.isPaid;

                        return (
                          <tr key={`${e.order}-${i}`}>
                            <td className="fw-bold">{e.order}</td>

                            <td>
                              {e.firstname} {e.lastname}
                              {e.hn && (
                                <div className="text-muted small">
                                  HN: {e.hn}
                                </div>
                              )}
                            </td>

                            <td>{e.healthInsurance || "-"}</td>

                            {/* Paid Status group */}
                            <td className="text-center align-middle">
                              <PaidIcon on={paidDiag} />
                            </td>
                            <td className="text-center align-middle">
                              <PaidIcon on={paidPost} />
                            </td>
                            <td className="text-center align-middle">
                              <PaidIcon on={paidCpap} />
                            </td>

                            {/* Phases (This Batch) group */}
                            <td className="text-center align-middle">
                              {editingSelectedBatch &&
                              phaseIndex("diagnosis") >= 0 ? (
                                <input
                                  type="number"
                                  min="0"
                                  className="form-control form-control-sm text-end"
                                  aria-label={`Diagnosis amount for ${e.order}`}
                                  value={
                                    e.phases[phaseIndex("diagnosis")].amount ||
                                    0
                                  }
                                  onChange={(event) =>
                                    updateBatchEditPhaseAmount(
                                      i,
                                      phaseIndex("diagnosis"),
                                      event.target.value,
                                    )
                                  }
                                />
                              ) : (
                                <Phase
                                  checked={hasPhase("diagnosis")}
                                  amount={phaseAmount("diagnosis")}
                                  disabled={true}
                                />
                              )}
                            </td>
                            <td className="text-center align-middle">
                              {editingSelectedBatch &&
                              phaseIndex("postConsult") >= 0 ? (
                                <input
                                  type="number"
                                  min="0"
                                  className="form-control form-control-sm text-end"
                                  aria-label={`Post-consult amount for ${e.order}`}
                                  value={
                                    e.phases[phaseIndex("postConsult")]
                                      .amount || 0
                                  }
                                  onChange={(event) =>
                                    updateBatchEditPhaseAmount(
                                      i,
                                      phaseIndex("postConsult"),
                                      event.target.value,
                                    )
                                  }
                                />
                              ) : (
                                <Phase
                                  checked={hasPhase("postConsult")}
                                  amount={phaseAmount("postConsult")}
                                  disabled={true}
                                />
                              )}
                            </td>
                            <td className="text-center align-middle">
                              {editingSelectedBatch &&
                              phaseIndex("cpapFollowup") >= 0 ? (
                                <input
                                  type="number"
                                  min="0"
                                  className="form-control form-control-sm text-end"
                                  aria-label={`Follow-up amount for ${e.order}`}
                                  value={
                                    e.phases[phaseIndex("cpapFollowup")]
                                      .amount || 0
                                  }
                                  onChange={(event) =>
                                    updateBatchEditPhaseAmount(
                                      i,
                                      phaseIndex("cpapFollowup"),
                                      event.target.value,
                                    )
                                  }
                                />
                              ) : (
                                <Phase
                                  checked={hasPhase("cpapFollowup")}
                                  amount={phaseAmount("cpapFollowup")}
                                  disabled={true}
                                />
                              )}
                            </td>

                            <td className="text-end fw-bold">
                              {money(rowTotal)} THB
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  <hr />

                  {editingSelectedBatch ? (
                    <div className="row justify-content-end">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="border rounded bg-light p-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span>Amount</span>
                            <strong>{money(batchEditTotals.amount)} THB</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span>WHT 3%</span>
                            <strong>- {money(batchEditTotals.wht)} THB</strong>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="mb-0">Deduction</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 160 }}
                              value={batchEdit.deduction}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  deduction: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <label className="mb-0">Adjustment Pay</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 160 }}
                              value={batchEdit.adjustmentPay}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  adjustmentPay: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="mb-3">
                            <label className="mb-1">Payment Note</label>
                            <textarea
                              className="form-control form-control-sm"
                              rows={3}
                              value={batchEdit.payNote}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  payNote: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <label className="mb-0">Pay Date</label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              style={{ width: 180 }}
                              value={batchEdit.payDate}
                              onChange={(event) =>
                                setBatchEdit((previous) => ({
                                  ...previous,
                                  payDate: event.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="border-top pt-3 d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-muted">Total Pay</span>
                              <h4 className="mb-0">
                                {money(batchEditTotals.totalPay)} THB
                              </h4>
                            </div>
                            <div>
                              <Button
                                variant="light"
                                size="sm"
                                className="mr-2"
                                onClick={() => setBatchEdit(null)}
                                disabled={savingBatchEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveBatchEdit}
                                disabled={savingBatchEdit}
                              >
                                {savingBatchEdit ? "Saving…" : "Save Changes"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="d-flex justify-content-end align-items-start"
                      style={{ gap: "20px" }}
                    >
                      <div className="text-right">
                        <div>
                          Amount:{" "}
                          <strong>
                            {money(selectedBatch.meta.amount || 0)} THB
                          </strong>
                        </div>
                        <div>
                          WHT 3%:{" "}
                          <strong>
                            - {money(selectedBatch.meta.wht || 0)} THB
                          </strong>
                        </div>
                        <div>
                          Deduction:{" "}
                          <strong>
                            - {money(selectedBatch.meta.deduction || 0)} THB
                          </strong>
                        </div>
                        <div>
                          Adjustment Pay:{" "}
                          <strong>
                            + {money(selectedBatch.meta.adjustmentPay || 0)} THB
                          </strong>
                        </div>
                        <div className="mt-2">
                          Total Pay:{" "}
                          <h3 className="d-inline">
                            <strong>
                              {money(selectedBatch.meta.totalPay || 0)} THB
                            </strong>
                          </h3>
                        </div>
                        <div className="mt-1">
                          Pay Date:{" "}
                          <strong>
                            {selectedBatch.meta.payDate.toDate
                              ? selectedBatch.meta.payDate
                                  .toDate()
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  })
                              : "-"}
                          </strong>
                        </div>
                        <div>
                          Payment Note:{" "}
                          <strong>{selectedBatch.meta.payNote || ""}</strong>
                        </div>
                      </div>

                      <div className="d-flex" style={{ gap: "8px" }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          style={{ height: "fit-content", padding: "10px 16px" }}
                          onClick={() => startBatchEdit(selectedBatch)}
                        >
                          Edit
                        </Button>
                        {paymentAccess && (
                          <Button
                            variant="danger"
                            size="sm"
                            style={{ height: "fit-content", padding: "10px 16px" }}
                            onClick={async () => {
                              try {
                                await confirmPaymentBatch(
                                  selectedBatch.doctorKey,
                                  selectedBatch.id,
                                );
                              } catch (e) {
                                console.error(e);
                                notifyError("Confirm failed", e.message || e);
                              }
                            }}
                          >
                            Confirm Payment
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Tab>}

        {paymentAccess && <Tab eventKey="confirmed" title="Confirmed Payments">
          {/* Ready to Send Email tab */}
          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-1">
                  Confirmed Payments{" "}
                  <span className="badge badge-success">{readyToEmail.length}</span>
                </h4>
                <small className="text-muted">
                  Review confirmation and email status for each completed batch.
                </small>
              </div>
              <Button
                variant="light"
                size="sm"
                onClick={loadReadyToEmailForDoctor}
                disabled={loadingReady}
              >
                Refresh
              </Button>
            </div>

            {loadingReady && <div>Loading…</div>}

            {!loadingReady && readyToEmail.length === 0 && (
              <div className="text-muted">
                No confirmed batches awaiting email.
              </div>
            )}

            {!loadingReady &&
              readyToEmail.length > 0 &&
              paymentAccess && (
                <Table
                  responsive
                  hover
                  className="table-striped table-sm mb-0"
                  style={{ minWidth: 920 }}
                >
                  <thead className="thead-light">
                    <tr>
                      <th>Status</th>
                      <th>Doctor</th>
                      <th>Payment period</th>
                      <th>Payment summary</th>
                      <th className="text-right">Net pay</th>
                      <th className="text-center">Cases</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...readyToEmail]
                      .sort(
                        (a, b) =>
                          paymentTimestamp(b.confirmedAt) -
                          paymentTimestamp(a.confirmedAt),
                      )
                      .map((b) => {
                        const meta = b.meta || {};
                        const doctor =
                          (doctorList.find(
                            (item) => item.doctorId === b.doctorKey,
                          ) || {}).name ||
                          b.doctorId ||
                          b.doctorKey ||
                          "-";

                        return (
                        <tr key={`${b.doctorKey}-${b.id}`} className="align-middle">
                          <td>
                            <span className="badge badge-success">Confirmed</span>
                            <small className="d-block text-muted mt-1">
                              {formatPaymentDate(b.confirmedAt, true)}
                            </small>
                            <span
                              className={`badge mt-1 ${
                                b.isSendEmail ? "badge-success" : "badge-light"
                              }`}
                            >
                              {b.isSendEmail ? "Email sent" : "Email pending"}
                            </span>
                          </td>
                          <td>
                            <div className="font-weight-bold">{doctor}</div>
                            <small className="text-muted">{b.doctorKey || "-"}</small>
                          </td>
                          <td>
                            <span className="badge badge-light">{b.month || "-"}</span>
                            <small className="d-block text-muted mt-1">
                              Pay: {formatPaymentDate(meta.payDate)}
                            </small>
                          </td>
                          <td>
                            <div>
                              Gross: <strong>{money(meta.amount || 0)} THB</strong>
                            </div>
                            <small className="text-muted">
                              WHT -{money(meta.wht || 0)} · Deduction -
                              {money(meta.deduction || 0)}
                            </small>
                          </td>
                          <td className="text-right">
                            <strong className="text-primary">
                              {money(meta.totalPay || 0)} THB
                            </strong>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-secondary">
                              {(b.entries || []).length}
                            </span>
                          </td>
                          <td className="text-right">
                            <div className="d-flex flex-wrap justify-content-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="mr-2 mb-1"
                                onClick={() => setSelectedBatch(b)}
                              >
                                Review
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="mr-2 mb-1"
                                onClick={() => exportBatchToExcel(b, selectedDoctor)}
                              >
                                Export Excel
                              </Button>{" "}
                              {canRollbackConfirmedBatch && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  className="mr-2 mb-1"
                                  onClick={() =>
                                    returnConfirmedBatchToPending(
                                      b.doctorKey,
                                      b.id,
                                    )
                                  }
                                  disabled={Boolean(rollingBackBatchKey)}
                                >
                                  {rollingBackBatchKey === `${b.doctorKey}/${b.id}`
                                    ? "Returning…"
                                    : "Return to Pending"}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant={b.isSendEmail ? "outline-success" : "success"}
                                className="mb-1"
                                onClick={() => handleSendEmail(b)}
                              >
                                {b.isSendEmail ? "Resend Email" : "Send Email"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </Table>
              )}

            {/* Optional: reuse the same Review card/modal you already built for pending batches */}
            {selectedBatch && (
              <div className="card mt-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Review Batch: {selectedBatch.id}</h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setSelectedBatch(null)}
                    >
                      Close
                    </Button>
                  </div>

                  {/* Reuse your grouped table (Paid Status + Phases) */}
                  {/* ... (the same review UI you already have) ... */}
                  <Table className="table-sm table-striped">
                    <thead>
                      <tr>
                        <th rowSpan="2" className="align-middle">
                          Order
                        </th>
                        <th rowSpan="2" className="align-middle">
                          Patient
                        </th>
                        <th rowSpan="2" className="align-middle">
                          Health Insurance
                        </th>

                        {/* Group 1: Paid Status snapshot from booking at draft time */}
                        <th colSpan="3" className="text-center">
                          Paid Status
                        </th>

                        {/* Group 2: Phases included in THIS batch */}
                        <th colSpan="3" className="text-center">
                          Phases (This Batch)
                        </th>

                        <th rowSpan="2" className="align-middle text-end">
                          Total (THB)
                        </th>
                      </tr>
                      <tr>
                        {/* Paid Status subcols */}
                        <th className="text-center">Diagnosis</th>
                        <th className="text-center">Post-consult</th>
                        <th className="text-center">Follow-up</th>
                        {/* Phases subcols */}
                        <th className="text-center">Diagnosis</th>
                        <th className="text-center">Post-consult</th>
                        <th className="text-center">Follow-up</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(selectedBatch.entries || []).map((e, i) => {
                        const rowTotal = (e.phases || []).reduce(
                          (s, p) => s + (Number(p.amount) || 0),
                          0,
                        );

                        // helpers for “this batch” phases
                        const hasPhase = (key) =>
                          (e.phases || []).some((p) => p.phase === key);
                        const phaseAmount = (key) =>
                          (e.phases || [])
                            .filter((p) => p.phase === key)
                            .reduce((s, p) => s + (Number(p.amount) || 0), 0);

                        // paid snapshot
                        const paid = e.paid || {};
                        const paidDiag = !!paid.diagnosis.isPaid;
                        const paidPost = !!paid.postConsult.isPaid;
                        const paidCpap = !!paid.cpapFollowup.isPaid;

                        return (
                          <tr key={`${e.order}-${i}`}>
                            <td className="fw-bold">{e.order}</td>

                            <td>
                              {e.firstname} {e.lastname}
                              {e.hn && (
                                <div className="text-muted small">
                                  HN: {e.hn}
                                </div>
                              )}
                            </td>

                            <td>{e.healthInsurance || "-"}</td>

                            {/* Paid Status group */}
                            <td className="text-center align-middle">
                              <PaidIcon on={paidDiag} />
                            </td>
                            <td className="text-center align-middle">
                              <PaidIcon on={paidPost} />
                            </td>
                            <td className="text-center align-middle">
                              <PaidIcon on={paidCpap} />
                            </td>

                            {/* Phases (This Batch) group */}
                            <td className="text-center align-middle">
                              <Phase
                                checked={hasPhase("diagnosis")}
                                amount={phaseAmount("diagnosis")}
                                disabled={true}
                              />
                            </td>
                            <td className="text-center align-middle">
                              <Phase
                                checked={hasPhase("postConsult")}
                                amount={phaseAmount("postConsult")}
                                disabled={true}
                              />
                            </td>
                            <td className="text-center align-middle">
                              <Phase
                                checked={hasPhase("cpapFollowup")}
                                amount={phaseAmount("cpapFollowup")}
                                disabled={true}
                              />
                            </td>

                            <td className="text-end fw-bold">
                              {money(rowTotal)} THB
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  <hr />

                  <div
                    className="d-flex justify-content-end align-items-start"
                    style={{ gap: "20px" }}
                  >
                    <div className="text-right">
                      <div>
                        Amount:{" "}
                        <strong>
                          {money(selectedBatch.meta.amount || 0)} THB
                        </strong>
                      </div>
                      <div>
                        WHT 3%:{" "}
                        <strong>
                          - {money(selectedBatch.meta.wht || 0)} THB
                        </strong>
                      </div>
                      <div>
                        Deduction:{" "}
                        <strong>
                          - {money(selectedBatch.meta.deduction || 0)} THB
                        </strong>
                      </div>
                      <div>
                        Adjustment Pay:{" "}
                        <strong>
                          + {money(selectedBatch.meta.adjustmentPay || 0)} THB
                        </strong>
                      </div>
                      <div className="mt-2">
                        Total Pay:{" "}
                        <h5 className="d-inline">
                          {money(selectedBatch.meta.totalPay || 0)} THB
                        </h5>
                      </div>
                      <div className="mt-1">
                        Pay Date:{" "}
                        <strong>
                          {selectedBatch.meta.payDate.toDate()
                            ? selectedBatch.meta.payDate
                                .toDate()
                                .toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                            : "-"}
                        </strong>
                      </div>
                      <div>
                        Payment Note:{" "}
                        <strong>{selectedBatch.meta.payNote || ""}</strong>
                      </div>
                    </div>

                    <Button
                      variant="success"
                      size="sm"
                      style={{ height: "fit-content", padding: "10px 16px" }}
                      onClick={() => handleSendEmail(selectedBatch)}
                    >
                      Send Email
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Tab>}
      </Tabs>
    </React.Fragment>
  );
};

export default DoctorDashboard;
